import * as clack from "@clack/prompts";
import { spawnSync } from "node:child_process";
import { readFileSync } from "node:fs";
import { hostname } from "node:os";
import { resolve } from "node:path";
import { Command } from "commander";
import pc from "picocolors";

declare const __PKG_VERSION__: string;

import { renderBanner } from "./banner.js";
import { DEFAULT_ORGX_BASE_URL, ORGX_WIZARD_AUTH_PATH } from "./constants.js";
import {
  acknowledgeBrowserPairing,
  openBrowser,
  startBrowserPairing,
  waitForBrowserPairing,
} from "./lib/browser-auth.js";
import {
  checkOrgxAuth,
  normalizeOrgxBaseUrl,
  resolveOrgxAuth,
  verifyOrgxAuth,
  type OrgxAuthCheckResult,
} from "./lib/auth.js";
import { clearWizardAuth, writeWizardAuth } from "./lib/auth-store.js";
import { startPkceLogin } from "./lib/oauth-client.js";
import {
  AGENT_ROSTER_DOMAINS,
  configureAgentRoster,
  ensureDefaultAgentRoster,
  listAgentRosterProfiles,
  type AgentRosterAssignment,
} from "./lib/agent-roster.js";
import { persistContinuityDefaults } from "./lib/continuity.js";
import { runFounderPreset, type FounderPresetResult } from "./lib/founder-preset.js";
import {
  FIRST_VALUE_INITIATIVE_TITLE,
  ensureFirstValueInitiative,
  ensureOnboardingTask,
  type FirstValueInitiativeResult,
} from "./lib/initiatives.js";
import {
  countPluginReportChanges,
  installOrgxPlugins,
  listOrgxPluginStatuses,
  uninstallOrgxPlugins,
  type OrgxPluginMutationReport,
  type OrgxPluginStatus,
} from "./lib/plugins.js";
import {
  addSkillExtension,
  getSkillStatus,
  installOrgxSkills,
  listSkillExtensions,
  setSkillExtensionEnabled,
  type SkillsInstallReport,
  type SkillExtension,
  type SkillExtensionWrite,
} from "./lib/skills.js";
import {
  buildLocalSkillExtensionContent,
  discoverLocalSkills,
  parseLocalSkillSources,
  selectLocalSkillCandidates,
  type LocalSkillCandidate,
} from "./lib/local-skill-discovery.js";
import { summarizeMutationResults } from "./lib/mutation-output.js";
import {
  HOSTED_MCP_OUTAGE_TITLE,
  getSetupVerificationHeadline,
  summarizeSetupVerification,
} from "./lib/setup-verification.js";
import {
  buildAgentRosterTelemetryProperties,
  buildDoctorTelemetryProperties,
  buildFirstValueInitiativeTelemetryProperties,
  buildOnboardingTaskTelemetryProperties,
  buildWorkspaceSetupTelemetryProperties,
} from "./lib/telemetry-events.js";
import { trackWizardTelemetry } from "./lib/telemetry.js";
import { captureWizardException, initializeWizardSentry } from "./lib/sentry.js";
import {
  clearWizardState,
  getOrCreateWizardInstallationId,
  hasSetupPromptSkip,
  readWizardState,
  recordSetupPromptSkip,
  type WizardAgentRosterRecord,
  type WizardFirstValueInitiativeRecord,
} from "./lib/wizard-state.js";
import {
  createWorkspace,
  getCurrentWorkspace,
  listWorkspaces,
  setDefaultWorkspace,
  type OrgxWorkspace,
} from "./lib/workspaces.js";
import type { WorkspaceConnectivityCheck } from "./lib/workspace-connectivity.js";
import {
  buildIntentHandoffUrl,
  createIntent,
  updateIntent,
  type AgentSlug,
  type Intent,
} from "./lib/intents.js";
import {
  DEFAULT_HOTKEYS,
  detectRenderCapabilities,
  dim as renderDim,
  isInteractive,
  normalizeHotkey,
  renderHotkeyBar,
  renderPlanPreview,
  renderRoutingLine,
  resolveConfidenceDecimal,
  type IntentAction,
} from "./lib/intent-render.js";
import { runWorkspaceSetup, type WorkspaceSetupResult } from "./lib/setup-workspace.js";
import {
  buildProfileHandoffPrompt,
  buildProfileSummary,
  resolveSetupProfile,
  supportedSetupProfileIds,
  type SetupProfile,
} from "./lib/setup-profiles.js";
import { runDailyBriefOnboarding } from "./lib/daily-brief-onboarding.js";
import {
  filterSupportedAiSessionSources,
  loadAiSessionImports,
  parseAiSessionSources,
} from "./lib/ai-session-import.js";
import {
  loadWorkGraphInvestigationSourceData,
  parseInvestigationSourceList,
} from "./lib/work-graph-source-adapters.js";
import {
  buildSelfAuditPlan,
  createAuditArtifact,
  createAuditFollowUpTask,
  createInitiativeFromAuditPlan,
  renderSelfAuditMarkdown,
  type AuditScores,
  type FounderLoopImport,
} from "./lib/self-audit.js";
import {
  defaultOperatingMapIdempotencyKey,
  proposeOperatingProcessFromMap,
  startOperatingMapDiscovery,
  type OperatingMapMode,
} from "./lib/operating-map.js";
import {
  buildNativeAiClientExtractions,
  buildWorkGraphExtractionProtocol,
  buildSessionReconciliationReport,
  renderWorkGraphExtractionProtocolMarkdown,
  renderWorkGraphMarkdown,
  type WorkGraphClientExtraction,
} from "./lib/work-graph.js";
import {
  buildAqProfileStory,
  renderAqAgentBrief,
} from "./lib/aq-profile-story.js";
import {
  publishWorkGraphEvents,
  publishWorkGraphReport,
} from "./lib/work-graph-publish.js";
import {
  buildWorkGraphHookReplayPatch,
  readRuntimeHookOutbox,
} from "./lib/work-graph-hook-events.js";
import {
  inspectRuntimeHooks,
  installRuntimeHooks,
  parseRuntimeHookTargets,
  parseRuntimeHookWorkEpisodeCapture,
} from "./lib/runtime-hooks.js";
import {
  flushSessionSummaryCaptures,
  sessionSummaryCaptureFiles,
  type FlushSessionSummaryCapturesResult,
} from "./lib/session-summary-queue.js";
import { claimSessionSummaryFlushLease } from "./lib/session-summary-flush-lease.js";
import { createOrgxSpinner } from "./spinner.js";
import { detectSurface } from "./surfaces/detection.js";
import {
  assessDoctorReport,
  addMcpSurface,
  addSurface,
  listSurfaceStatuses,
  removeMcpSurface,
  removeSurface,
  runDoctor,
  setupDetectedSurfaces,
  type SurfaceMutationResult,
  type SurfaceStatus,
} from "./surfaces/registry.js";
import {
  readWorkloadDiagnosisInput,
  requestWorkloadDiagnosis,
  type WorkloadDiagnosisResponse,
} from "./lib/workload-diagnosis.js";
import { fileExists, isRecord, writeJsonFile, writeTextFile } from "./lib/fs.js";
import { fetchWithRetry } from "./lib/network.js";
import {
  buildMinimalCloudSummary,
  distillSpool,
  postSummaries,
  truncateSpool,
  type BackfillPostResult,
  type TruncateResult,
} from "./lib/session-summary-backfill.js";
import { SESSION_SUMMARY_ENDPOINT_PATH } from "./lib/session-summary-hook.js";
import {
  clearSessionWorkContextActivation,
  createSessionWorkContextActivation,
  inspectSessionWorkContextActivation,
  sessionWorkContextActivationPath,
  writeSessionWorkContextActivation,
  type SessionWorkContextActivationInspection,
} from "./lib/session-work-context.js";
import { launchDeepseekWithOrgx } from "./lib/deepseek-launcher.js";

const ICON = {
  ok: pc.green("✓"),
  err: pc.red("✗"),
  warn: pc.yellow("!"),
  skip: pc.dim("·"),
} as const;

/**
 * Returns the right command prefix based on how the wizard was invoked.
 * When run via `npx @useorgx/wizard`, `orgx-wizard` won't be in PATH — hints
 * should use the npx form so users can copy-paste them directly.
 */
function getCmd(): string {
  const argv1 = process.argv[1] ?? "";
  return argv1.includes("_npx") || argv1.includes("/.npm/") || argv1.includes("/npm/_npx")
    ? "npx @useorgx/wizard"
    : "orgx-wizard";
}

function formatAuthSource(source: OrgxAuthCheckResult["source"]): string {
  switch (source) {
    case "environment":
      return "env ORGX_API_KEY";
    case "wizard-store":
      return "wizard auth store";
    case "openclaw-config-file":
      return "openclaw config";
    default:
      return "not configured";
  }
}

function formatPluginTargetLabel(target: OrgxPluginStatus["target"]): string {
  switch (target) {
    case "cursor":
      return "Cursor plugin";
    case "claude":
      return "Claude Code";
    case "codex":
      return "Codex";
    case "opencode":
      return "OpenCode";
    case "openclaw":
      return "OpenClaw";
  }
}

function printSurfaceTable(statuses: SurfaceStatus[]): void {
  for (const status of statuses) {
    const icon = status.configured ? ICON.ok : status.detected ? ICON.warn : ICON.skip;
    const state = status.configured
      ? pc.green("configured")
      : status.detected
        ? pc.yellow("detected")
        : pc.dim("not found");
    const mode = pc.dim(status.mode === "automated" ? "auto  " : "manual");
    console.log(`  ${icon}  ${pc.bold(status.name.padEnd(10))} ${mode}  ${state}`);
    if (status.mode === "manual") {
      console.log(`       ${pc.dim(status.summary)}`);
    }
  }
}

function printPluginStatusReport(statuses: OrgxPluginStatus[]): void {
  for (const status of statuses) {
    const icon = status.installed ? ICON.ok : status.available ? ICON.warn : ICON.skip;
    const state = status.installed
      ? pc.green("installed")
      : status.available
        ? pc.yellow("available")
        : pc.dim("not found");
    console.log(
      `  ${icon}  ${pc.bold(formatPluginTargetLabel(status.target).padEnd(12))} ${state}  ${pc.dim(status.message)}`,
    );
  }
}

function printMutationResults(results: SurfaceMutationResult[]): void {
  for (const result of summarizeMutationResults(results)) {
    const icon = result.state === "updated" ? ICON.ok : ICON.skip;
    const state = result.state === "updated" ? pc.green("updated  ") : pc.dim("unchanged");
    console.log(`  ${icon}  ${pc.bold(result.name.padEnd(10))} ${state}  ${pc.dim(result.message)}`);
  }
}

function printSurfaceSummary(results: SurfaceMutationResult[]): void {
  const summarized = summarizeMutationResults(results);
  if (summarized.length === 0) {
    // No supported editors found — don't claim "0 surfaces already configured".
    console.log(
      `  ${ICON.skip}  ${pc.dim("No supported AI tools detected on this machine.")}`,
    );
    console.log(
      `  ${pc.dim("→")}  ${pc.dim("Install Claude, Cursor, Codex, DeepSeek Harness, VS Code, Windsurf, or Zed, then re-run setup.")}`,
    );
    return;
  }
  const updated = summarized.filter((r) => r.state === "updated");
  if (updated.length === 0) {
    console.log(
      `  ${ICON.skip}  ${pc.dim(`${summarized.length} surface${summarized.length === 1 ? "" : "s"} already configured`)}`,
    );
    return;
  }
  for (const result of summarized) {
    const icon = result.state === "updated" ? ICON.ok : ICON.skip;
    const state = result.state === "updated" ? pc.green("updated  ") : pc.dim("unchanged");
    console.log(`  ${icon}  ${pc.bold(result.name.padEnd(10))} ${state}  ${pc.dim(result.message)}`);
  }
}

function printPluginMutationReport(report: OrgxPluginMutationReport): void {
  for (const result of report.results) {
    const icon = result.changed ? ICON.ok : ICON.skip;
    const state = result.changed ? pc.green("updated  ") : pc.dim("unchanged");
    console.log(
      `  ${icon}  ${pc.bold(formatPluginTargetLabel(result.target).padEnd(12))} ${state}  ${pc.dim(result.message)}`,
    );
  }
}

interface AuditCommandOptions {
  attachToInitiative?: string;
  attachArtifact?: boolean;
  artifactUrl?: string;
  claudeProjectsDir?: string;
  codexSessionsDir?: string;
  createInitiative?: boolean;
  dryRun?: boolean;
  entityId?: string;
  entityType?: "project" | "initiative" | "milestone" | "task" | "decision";
  extraction?: string[];
  from?: string;
  input?: string;
  json?: boolean;
  milestoneId?: string;
  outputDir?: string;
  publicShare?: boolean;
  publish?: boolean;
  sourceLabel?: string;
  sessionDays?: string;
  sessionLimit?: string;
  workspaceId?: string;
  workspaceName?: string;
  workstreamId?: string;
  writeFollowUp?: boolean;
  yes?: boolean;
}

interface OperatingMapCommandOptions {
  candidate?: string;
  deepSearch?: boolean;
  idempotencyKey?: string;
  json?: boolean;
  source?: string;
  workspaceId?: string;
  yes?: boolean;
}

type WorkGraphCommandOptions = AuditCommandOptions;

interface HooksCommandOptions {
  background?: boolean;
  cwd?: string;
  fingerprint?: string;
  json?: boolean;
  limit?: string;
  outbox?: string;
  queue?: string;
  targets?: string;
  workCapture?: string;
  yes?: boolean;
}

interface SessionContextCommandOptions {
  contextSha256?: string;
  cwd?: string;
  file?: string;
  json?: boolean;
  sessionId?: string;
  sourceClient?: string;
  ttlMinutes?: string;
}

interface HooksBackfillCommandOptions {
  json?: boolean;
  post?: boolean;
  spool?: string;
  truncate?: boolean;
  yes?: boolean;
}

interface DoctorCommandOptions {
  baseUrl?: string;
  workload?: string;
  json?: boolean;
}

interface WorkGraphExtractionSchemaCommandOptions {
  json?: boolean;
  output?: string;
}

interface WorkGraphRuntimeEventCommandOptions {
  cwd?: string;
  eventKind?: string;
  json?: boolean;
  message?: string;
  outputDir?: string;
  role?: string;
  source: string;
  summary?: string;
  toolName?: string;
}

function formatScoreLine(scores: AuditScores): string {
  return Object.entries(scores)
    .map(([dimension, score]) => `${dimension}=${score}`)
    .join(" ");
}

function printRuntimeHookInspection(report: ReturnType<typeof inspectRuntimeHooks>): void {
  console.log(`  ${report.installed.hookScript ? ICON.ok : ICON.warn}  ${pc.bold("hook script  ")} ${report.installed.hookScript ? pc.green("installed") : pc.yellow("missing")}  ${pc.dim(report.paths.hookScriptPath)}`);
  console.log(`  ${report.installed.codex ? ICON.ok : ICON.warn}  ${pc.bold("Codex       ")} ${report.installed.codex ? pc.green("installed") : pc.yellow("missing")}  ${pc.dim(report.paths.codexHooksPath)}`);
  console.log(`  ${report.codex.hooksEnabled ? ICON.ok : ICON.warn}  ${pc.bold("Codex flag  ")} ${report.codex.hooksEnabled ? pc.green("enabled") : pc.yellow("not enabled")}  ${pc.dim(report.paths.codexConfigPath)}`);
  console.log(`  ${report.codex.notifyPreserved ? ICON.ok : ICON.warn}  ${pc.bold("notify      ")} ${report.codex.notifyPreserved ? pc.green("preserved") : pc.yellow("check config")}  ${pc.dim(report.codex.hasNotify ? "existing notify detected" : "no notify entry")}`);
  console.log(`  ${report.installed.claudeCode ? ICON.ok : ICON.warn}  ${pc.bold("Claude Code ")} ${report.installed.claudeCode ? pc.green("installed") : pc.yellow("missing")}  ${pc.dim(report.paths.claudeSettingsPath)}`);
  const summaryHookState = report.installed.summaryHookScriptStatus === "current"
    ? pc.green("current")
    : pc.yellow(report.installed.summaryHookScriptStatus);
  console.log(`  ${report.installed.summaryHookScript ? ICON.ok : ICON.warn}  ${pc.bold("summary hook")} ${summaryHookState}  ${pc.dim(report.paths.summaryHookScriptPath)}`);
  console.log(`  ${report.installed.automaticDelivery ? ICON.ok : ICON.warn}  ${pc.bold("auto delivery")} ${report.installed.automaticDelivery ? pc.green("configured") : pc.yellow("not configured")}  ${pc.dim("detached, ACK-gated worker")}`);
  console.log(`  ${ICON.skip}  ${pc.bold("capture queue")} ${pc.dim(`${report.sessionSummaryCaptures} capture${report.sessionSummaryCaptures === 1 ? "" : "s"} at ${report.paths.sessionSummaryQueueDir}`)}`);
  console.log(`  ${report.sessionSummaryQuarantinedCaptures === 0 ? ICON.skip : ICON.warn}  ${pc.bold("quarantine   ")} ${pc.dim(`${report.sessionSummaryQuarantinedCaptures} malformed capture${report.sessionSummaryQuarantinedCaptures === 1 ? "" : "s"}, recoverable on disk`)}`);
  console.log(`  ${ICON.skip}  ${pc.bold("outbox      ")} ${pc.dim(`${report.outboxEvents} event${report.outboxEvents === 1 ? "" : "s"} at ${report.paths.outboxPath}`)}`);
  const contextIcon = report.workContext.ready ? ICON.ok : ICON.warn;
  const contextState = report.workContext.ready
    ? pc.green("active")
    : pc.yellow(report.workContext.state.replaceAll("_", " "));
  console.log(`  ${contextIcon}  ${pc.bold("work context")} ${contextState}  ${pc.dim(report.workContext.ready ? `until ${report.workContext.validUntil}` : report.workContext.path)}`);
  for (const client of report.continuity.clients) {
    const readiness = [
      `capture ${client.captureReady ? "ready" : "blocked"}`,
      `first-turn ${client.firstTurnContextReady ? "ready" : "blocked"}`,
      `receipt ${client.receiptReady ? "ready" : "blocked"}`,
    ].join(" · ");
    const state = client.receiptReady ? pc.green(readiness) : pc.yellow(readiness);
    console.log(`  ${client.receiptReady ? ICON.ok : ICON.warn}  ${pc.bold(client.client.padEnd(12))} ${state}  ${pc.dim(client.adapterVersion ?? client.terminalBoundary)}`);
  }
}

function printSessionWorkContextInspection(
  report: SessionWorkContextActivationInspection,
): void {
  const icon = report.ready ? ICON.ok : ICON.warn;
  const state = report.ready
    ? pc.green("receipt context active")
    : pc.yellow(report.state.replaceAll("_", " "));
  console.log(`  ${icon}  ${pc.bold("context     ")} ${state}`);
  console.log(`  ${ICON.skip}  ${pc.bold("cwd         ")} ${pc.dim(report.cwd)}`);
  console.log(`  ${ICON.skip}  ${pc.bold("valid until ")} ${pc.dim(report.validUntil ?? "not active")}`);
  console.log(`  ${ICON.skip}  ${pc.bold("record      ")} ${pc.dim(report.path)}`);
}

function printSessionSummaryFlush(result: FlushSessionSummaryCapturesResult): void {
  if (result.skipped) {
    console.log(`  ${ICON.skip}  ${pc.bold("captures    ")} ${pc.dim(result.skipped.replaceAll("_", " "))}`);
    return;
  }
  const delivered = `${result.acknowledged}/${result.attempted} acknowledged`;
  console.log(`  ${result.retained === 0 && result.malformed === 0 ? ICON.ok : ICON.warn}  ${pc.bold("captures    ")} ${pc.dim(`${delivered}, ${result.retained} retained, ${result.quarantined} quarantined, ${result.malformed} malformed`)}`);
  if (result.fallbackAcknowledged > 0) {
    console.log(`  ${ICON.warn}  ${pc.bold("fallback    ")} ${pc.dim(`${result.fallbackAcknowledged} capture(s) delivered through the legacy Work Graph endpoint`)}`);
  }
  if (result.receiptExpected > 0) {
    const receiptStatus = `${result.receiptAcknowledged}/${result.receiptExpected} persisted`;
    const receiptIcon =
      result.receiptAcknowledged === result.receiptExpected
        ? ICON.ok
        : ICON.warn;
    console.log(
      `  ${receiptIcon}  ${pc.bold("receipts    ")} ${pc.dim(receiptStatus)}`
    );
  }
  if (result.firstError) console.log(`  ${ICON.warn}  ${pc.dim(result.firstError)}`);
}

async function runSessionSummaryFlushCommand(options: HooksCommandOptions): Promise<void> {
  const queueDir = resolve(options.queue?.trim() || inspectRuntimeHooks().paths.sessionSummaryQueueDir);
  const found = sessionSummaryCaptureFiles(queueDir).length;
  let result: FlushSessionSummaryCapturesResult;

  if (found === 0) {
    result = {
      found: 0,
      attempted: 0,
      acknowledged: 0,
      retained: 0,
      malformed: 0,
      quarantined: 0,
      fallbackAcknowledged: 0,
      receiptExpected: 0,
      receiptAcknowledged: 0,
      skipped: "empty_queue",
    };
  } else {
    const lease = claimSessionSummaryFlushLease(queueDir);
    if (!lease.acquired) {
      if (lease.reason === "unavailable") {
        if (options.background) return;
        throw new Error(`Could not acquire the session-summary flush lease at ${queueDir}.`);
      }
      result = {
        found,
        attempted: 0,
        acknowledged: 0,
        retained: 0,
        malformed: 0,
        quarantined: 0,
        fallbackAcknowledged: 0,
        receiptExpected: 0,
        receiptAcknowledged: 0,
        skipped: "already_running",
      };
    } else {
      try {
        const auth = await resolveOrgxAuth();
        if (!auth) {
          if (options.background) return;
          throw new Error("No OrgX credential found. Run `orgx-wizard login` first.");
        }
        result = await flushSessionSummaryCaptures({
          queueDir,
          auth,
          limit: parsePositiveInt(options.limit, 100),
          stopOnFailure: options.background === true,
          send: async (url, body, headers) => {
            try {
              const response = await fetchWithRetry(
                url,
                {
                  method: "POST",
                  headers,
                  body: JSON.stringify(body),
                },
                options.background
                  ? { timeoutMs: 8_000, retries: 0 }
                  : { timeoutMs: 12_000, retries: 1 },
              );
              let responseBody: unknown;
              try {
                responseBody = await response.json();
              } catch {
                responseBody = undefined;
              }
              return {
                ok: response.ok,
                status: response.status,
                body: responseBody,
              };
            } catch {
              return { ok: false, status: 0 };
            }
          },
        });
      } finally {
        lease.release();
      }
    }
  }

  if (options.background) return;

  await safeTrackWizardTelemetry("hooks_session_summary_flush_ran", {
    command: "hooks flush",
    attempted: String(result.attempted),
    acknowledged: String(result.acknowledged),
    retained: String(result.retained),
    quarantined: String(result.quarantined),
  });
  if (result.retained > 0 || result.malformed > 0) process.exitCode = 1;

  if (options.json) {
    console.log(JSON.stringify({ ok: result.retained === 0 && result.malformed === 0, queueDir, ...result }, null, 2));
    return;
  }
  printSessionSummaryFlush(result);
}

/**
 * Distill the historical raw spool into lean session summaries.
 *
 * Read-only by default. `--post` ships the summaries, `--truncate` archives the
 * spool afterwards, and both require `--yes`: uploading or truncating a user's
 * own work history is their decision, not the CLI's.
 */
async function runHookBackfillCommand(options: HooksBackfillCommandOptions): Promise<void> {
  const paths = inspectRuntimeHooks().paths;
  const spoolPath = resolve(options.spool?.trim() || paths.outboxPath);

  if (!fileExists(spoolPath)) {
    const message = `No hook spool found at ${spoolPath}`;
    if (options.json) {
      console.log(JSON.stringify({ ok: true, distilled: 0, reason: "missing_spool", spoolPath }, null, 2));
      return;
    }
    console.log(`  ${ICON.skip}  ${pc.dim(message)}`);
    return;
  }

  if ((options.post || options.truncate) && !options.yes) {
    throw new Error("Posting or truncating the hook spool requires --yes. Run without --post to preview.");
  }
  if (options.truncate && !options.post) {
    throw new Error("--truncate is only valid together with --post; nothing may be discarded unsent.");
  }

  const distilled = await distillSpool(spoolPath);
  const reduction = distilled.bytes.total > 0
    ? Math.round(distilled.spoolBytes / distilled.bytes.total)
    : 0;

  let post: BackfillPostResult = { attempted: 0, posted: 0, failed: 0 };
  let truncation: TruncateResult = { truncated: false, bytesReleased: 0 };

  if (options.post) {
    const auth = await resolveOrgxAuth();
    if (!auth) {
      throw new Error("No OrgX credential found. Run `orgx-wizard login` first.");
    }
    const url = `${auth.baseUrl.replace(/\/+$/, "")}${SESSION_SUMMARY_ENDPOINT_PATH}`;
    post = await postSummaries(distilled.summaries, async (summary) => {
      const minimalSummary = buildMinimalCloudSummary(summary, {
        captureKind: "historical_backfill",
      });
      const response = await fetchWithRetry(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.apiKey}` },
        body: JSON.stringify({ session: minimalSummary, backfill: true }),
      });
      return { ok: response.ok, status: response.status };
    });

    if (options.truncate) {
      truncation = truncateSpool(spoolPath, post);
    }
  }

  const result = {
    ok: true,
    spoolPath,
    spoolBytes: distilled.spoolBytes,
    lines: distilled.lines,
    badLines: distilled.badLines,
    sessions: distilled.summaries.length,
    payloadBytes: distilled.bytes,
    reductionFactor: reduction,
    posted: options.post ? post : null,
    truncated: options.truncate ? truncation : null,
  };

  if (options.json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`  ${ICON.ok}  ${pc.bold("spool       ")} ${pc.dim(`${(distilled.spoolBytes / 1_048_576).toFixed(1)}MB, ${distilled.lines} lines, ${distilled.badLines} unparsable`)}`);
  console.log(`  ${ICON.ok}  ${pc.bold("distilled   ")} ${pc.dim(`${distilled.summaries.length} sessions, ${(distilled.bytes.total / 1024).toFixed(0)}KB total (p50 ${distilled.bytes.p50}B, max ${distilled.bytes.max}B), ${reduction}x smaller`)}`);
  if (!options.post) {
    console.log(`  ${ICON.skip}  ${pc.dim("preview only. Re-run with --post --yes to upload, and add --truncate to archive the spool.")}`);
    return;
  }
  console.log(`  ${post.failed === 0 ? ICON.ok : ICON.warn}  ${pc.bold("posted      ")} ${pc.dim(`${post.posted}/${post.attempted} sessions${post.firstError ? ` (${post.firstError})` : ""}`)}`);
  if (options.truncate) {
    console.log(`  ${truncation.truncated ? ICON.ok : ICON.warn}  ${pc.bold("truncated   ")} ${pc.dim(truncation.truncated ? `${(truncation.bytesReleased / 1_048_576).toFixed(1)}MB archived to ${truncation.archivePath}` : `skipped: ${truncation.reason}`)}`);
  }
}

function requireHookReplayApproval(options: HooksCommandOptions, interactive: boolean): Promise<boolean> | boolean {
  if (options.yes) return true;
  if (!interactive) {
    throw new Error("Replaying Work Graph hook events requires --yes in non-interactive mode.");
  }

  return clack.confirm({
    message: "Replay passive hook events into this claimed Work Graph profile?",
  }).then((value) => {
    if (clack.isCancel(value) || value !== true) {
      clack.cancel("Hook replay cancelled.");
      return false;
    }
    return true;
  });
}

function parsePositiveInt(value: string | undefined, fallback: number): number {
  if (!value?.trim()) return fallback;
  const parsed = Number.parseInt(value.trim(), 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error(`Expected a positive integer, got ${value}`);
  }
  return parsed;
}

async function runHookReplayCommand(options: HooksCommandOptions): Promise<void> {
  const fingerprint = options.fingerprint?.trim();
  if (!fingerprint) {
    throw new Error("Missing --fingerprint <wgf_...> for hook event replay.");
  }

  const interactive = Boolean(process.stdin.isTTY && process.stdout.isTTY);
  const paths = inspectRuntimeHooks().paths;
  const outboxPath = resolve(options.outbox?.trim() || paths.outboxPath);
  const readResult = readRuntimeHookOutbox(outboxPath, parsePositiveInt(options.limit, 200));
  const replay = buildWorkGraphHookReplayPatch(readResult);

  if (replay.records === 0) {
    if (options.json) {
      console.log(JSON.stringify({ ok: true, published: false, reason: "empty_outbox", ...replay }, null, 2));
      return;
    }
    console.log(`  ${ICON.skip}  ${pc.bold("outbox      ")} ${pc.dim(`no hook events found at ${outboxPath}`)}`);
    return;
  }

  const approved = await requireHookReplayApproval(options, interactive);
  if (!approved) return;

  const result = await publishWorkGraphEvents(fingerprint, replay.patch);
  if (!result.ok) process.exitCode = 1;

  await safeTrackWizardTelemetry("hooks_replay_ran", {
    command: "hooks replay",
    fingerprint,
    records: String(replay.records),
    sources: replay.sources.join(","),
    status: String(result.status),
  });

  if (options.json) {
    console.log(JSON.stringify({ ...replay, published: result }, null, 2));
    return;
  }

  console.log(`  ${result.ok ? ICON.ok : ICON.warn}  ${pc.bold("replayed    ")} ${replay.records} hook event${replay.records === 1 ? "" : "s"}  ${pc.dim(`status ${result.status}`)}`);
  console.log(`  ${ICON.skip}  ${pc.bold("sources     ")} ${pc.dim(replay.sources.join(", ") || "none")}`);
  console.log(`  ${ICON.skip}  ${pc.bold("evidence    ")} ${pc.dim(`${replay.evidenceRefs} public-summary ref${replay.evidenceRefs === 1 ? "" : "s"}`)}`);
}

function readAuditInput(options: AuditCommandOptions, interactive: boolean): Promise<string> | string {
  if (options.input?.trim()) {
    return readFileSync(resolve(options.input.trim()), "utf8");
  }

  if (!process.stdin.isTTY) {
    return readFileSync(0, "utf8");
  }

  if (!interactive) {
    throw new Error("Audit input is required. Pass --input <file> or pipe text into wizard audit.");
  }

  return textPrompt({
    message: "Paste a short AI-session summary, decision log, or founder context excerpt",
    placeholder: "Decision: Founder Loop first. Artifact: proof ledger attached. Next action: ...",
    validate: (value) => value?.trim().length ? undefined : "Audit context is required.",
  }).then((value) => {
    if (clack.isCancel(value) || typeof value !== "string") {
      clack.cancel("Audit cancelled.");
      return "";
    }
    return value;
  });
}

function parsePositiveInteger(value: string | undefined, fallback: number, label: string): number {
  if (!value?.trim()) return fallback;

  const parsed = Number.parseInt(value.trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${label} must be a positive integer.`);
  }

  return parsed;
}

function collectPathOption(value: string, previous: string[] = []): string[] {
  return [
    ...previous,
    ...value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
  ];
}

function parseClientExtractionFile(path: string): WorkGraphClientExtraction {
  const resolvedPath = resolve(path);
  const parsed = JSON.parse(readFileSync(resolvedPath, "utf8")) as unknown;
  if (!isRecord(parsed)) {
    throw new Error(`AI-client extraction must be a JSON object: ${resolvedPath}`);
  }
  if (!Array.isArray(parsed.findings)) {
    throw new Error(`AI-client extraction must include findings[]: ${resolvedPath}`);
  }
  if (typeof parsed.source_client !== "string" || !parsed.source_client.trim()) {
    throw new Error(`AI-client extraction must include source_client: ${resolvedPath}`);
  }

  return parsed as unknown as WorkGraphClientExtraction;
}

function readClientExtractions(options: WorkGraphCommandOptions): WorkGraphClientExtraction[] {
  return (options.extraction ?? []).map(parseClientExtractionFile);
}

async function readAuditImports(
  options: AuditCommandOptions,
  interactive: boolean,
): Promise<{
  connectedSources: string[];
  imports: FounderLoopImport[];
  missingSources: string[];
}> {
  const sources = parseAiSessionSources(options.from);
  const imports: FounderLoopImport[] = [];
  const connectedSources: string[] = [];
  const missingSources: string[] = [];

  if (sources.length > 0) {
    const imported = loadAiSessionImports({
      ...(options.claudeProjectsDir?.trim() ? { claudeProjectsDir: resolve(options.claudeProjectsDir.trim()) } : {}),
      ...(options.codexSessionsDir?.trim() ? { codexSessionsDir: resolve(options.codexSessionsDir.trim()) } : {}),
      limitPerSource: parsePositiveInteger(options.sessionLimit, 3, "--session-limit"),
      sinceDays: parsePositiveInteger(options.sessionDays, 30, "--session-days"),
      sources,
    });
    imports.push(...imported.imports);
    connectedSources.push(...imported.connectedSources);
    missingSources.push(...imported.missingSources);
  }

  const shouldReadManualInput = Boolean(options.input?.trim()) || sources.length === 0 || !process.stdin.isTTY;
  if (shouldReadManualInput) {
    const text = (await readAuditInput(options, interactive)).trim();
    if (text) {
      imports.push({
        sourceId: "wizard-audit-input",
        sourceLabel: options.sourceLabel?.trim() || "Wizard audit input",
        metadata: {
          message_count: 1,
          retained_line_count: text.split(/\r?\n/).filter(Boolean).length,
          source_client: "manual",
        },
        text,
      });
      connectedSources.push(options.sourceLabel?.trim() || "Manual AI-session import");
    }
  }

  if (imports.length === 0) {
    const sourceHint = sources.length > 0
      ? ` No audit-relevant lines were found in ${sources.join(", ")} sessions.`
      : "";
    throw new Error(`Audit input is required.${sourceHint} Pass --input <file>, pipe text, or use --from claude,codex,opencode,goose,cursor,github,slack,all.`);
  }

  return {
    connectedSources,
    imports,
    missingSources,
  };
}

async function readWorkGraphInputs(
  options: WorkGraphCommandOptions,
  interactive: boolean,
): Promise<{
  clientExtractions: WorkGraphClientExtraction[];
  connectedSources: string[];
  investigationRawEvents: Awaited<ReturnType<typeof loadWorkGraphInvestigationSourceData>>["rawEvents"];
  imports: FounderLoopImport[];
  missingSources: string[];
}> {
  const clientExtractions = readClientExtractions(options);
  const investigationSources = parseInvestigationSourceList(options.from);
  const legacySessionSources = filterSupportedAiSessionSources(investigationSources);
  const investigationSourceData = investigationSources.length > 0
    ? loadWorkGraphInvestigationSourceData({
        ...(options.claudeProjectsDir?.trim() ? { claudeProjectsDir: resolve(options.claudeProjectsDir.trim()) } : {}),
        ...(options.codexSessionsDir?.trim() ? { codexSessionsDir: resolve(options.codexSessionsDir.trim()) } : {}),
        cwd: process.cwd(),
        limitPerSource: parsePositiveInteger(options.sessionLimit, 8, "--session-limit"),
        sinceDays: parsePositiveInteger(options.sessionDays, 45, "--session-days"),
        sources: investigationSources,
      })
    : {
        clientExtractions: [],
        connectedSources: [],
        missingSources: [],
        rawEvents: [],
      };
  const shouldReadSessionImports = Boolean(
    legacySessionSources.length > 0 ||
    options.from?.trim().toLowerCase() === "manual" ||
    options.input?.trim() ||
    (clientExtractions.length === 0 && investigationSourceData.clientExtractions.length === 0) ||
    (!process.stdin.isTTY && clientExtractions.length === 0 && investigationSourceData.clientExtractions.length === 0),
  );

  if (!shouldReadSessionImports) {
    return {
      clientExtractions: [...clientExtractions, ...investigationSourceData.clientExtractions],
      connectedSources: [],
      investigationRawEvents: investigationSourceData.rawEvents,
      imports: [],
      missingSources: [],
    };
  }

  let auditImports: Awaited<ReturnType<typeof readAuditImports>>;
  try {
    const { from: _investigationFrom, ...legacyOptions } = options;
    auditImports = await readAuditImports({
      ...legacyOptions,
      ...(legacySessionSources.length > 0
        ? { from: legacySessionSources.join(",") }
        : {}),
    }, interactive);
  } catch (error) {
    if (clientExtractions.length === 0 && investigationSourceData.clientExtractions.length === 0) throw error;
    const message = error instanceof Error ? error.message : String(error);
    auditImports = {
      connectedSources: [],
      imports: [],
      missingSources: [`fallback session import: ${message}`],
    };
  }
  return {
    clientExtractions: [...clientExtractions, ...investigationSourceData.clientExtractions],
    connectedSources: [...auditImports.connectedSources, ...investigationSourceData.connectedSources],
    investigationRawEvents: investigationSourceData.rawEvents,
    imports: auditImports.imports,
    missingSources: [...auditImports.missingSources, ...investigationSourceData.missingSources],
  };
}

function requireWriteApproval(options: AuditCommandOptions, interactive: boolean): Promise<boolean> | boolean {
  const wantsWrite = Boolean(options.createInitiative || options.attachToInitiative || options.writeFollowUp);
  if (!wantsWrite || options.yes || options.dryRun) return true;

  if (!interactive) {
    throw new Error("Write flags require --yes in non-interactive mode.");
  }

  return clack.confirm({
    message: "Approve OrgX writes for this audit run?",
  }).then((value) => {
    if (clack.isCancel(value) || value !== true) {
      clack.cancel("Audit writeback cancelled.");
      return false;
    }
    return true;
  });
}

function requireWorkGraphPublishApproval(options: WorkGraphCommandOptions, interactive: boolean): Promise<boolean> | boolean {
  const wantsPublish = Boolean(options.publish || options.publicShare);
  if (!wantsPublish || options.yes || options.dryRun) return true;

  if (!interactive) {
    throw new Error("Publishing a Work Graph requires --yes in non-interactive mode.");
  }

  return clack.confirm({
    message: "Publish this Work Graph to OrgX and create a shareable profile?",
  }).then((value) => {
    if (clack.isCancel(value) || value !== true) {
      clack.cancel("Work Graph publish cancelled.");
      return false;
    }
    return true;
  });
}

async function resolveAuditWorkspace(
  options: AuditCommandOptions,
): Promise<{ id: string; name: string }> {
  const explicitId = options.workspaceId?.trim();
  const explicitName = options.workspaceName?.trim();
  if (explicitId || explicitName) {
    return {
      id: explicitId || "manual-workspace",
      name: explicitName || explicitId || "Manual workspace",
    };
  }

  try {
    const workspace = await getCurrentWorkspace();
    if (workspace) {
      return {
        id: workspace.id,
        name: workspace.name,
      };
    }
  } catch {
    // Local-only audits should still work without OrgX auth. The failed current
    // workspace lookup becomes visible through the missing source finding.
  }

  return {
    id: "local-workspace",
    name: "Local workspace",
  };
}

async function runAuditCommand(options: AuditCommandOptions): Promise<void> {
  const interactive = Boolean(process.stdin.isTTY && process.stdout.isTTY);
  const auditImports = await readAuditImports(options, interactive);

  const workspace = await resolveAuditWorkspace(options);
  const plan = buildSelfAuditPlan({
    connectedSources: [
      ...auditImports.connectedSources,
      ...(workspace.id === "local-workspace" ? [] : ["OrgX workspace"]),
    ],
    generatedAt: new Date().toISOString(),
    imports: auditImports.imports,
    missingSources: [
      ...(workspace.id === "local-workspace" ? ["OrgX workspace auth"] : []),
      ...auditImports.missingSources,
    ],
    workspace,
  });
  const markdown = renderSelfAuditMarkdown(plan);
  const outputDir = resolve(options.outputDir?.trim() || ".orgx/audits");
  const timestamp = plan.generated_at.replace(/[:.]/g, "-");
  const jsonPath = resolve(outputDir, `ai-native-self-audit-${timestamp}.json`);
  const markdownPath = resolve(outputDir, `ai-native-self-audit-${timestamp}.md`);

  writeJsonFile(jsonPath, plan);
  writeTextFile(markdownPath, markdown);

  if (options.json) {
    console.log(JSON.stringify({ jsonPath, markdownPath, scores: plan.scores }, null, 2));
  } else {
    console.log(`  ${ICON.ok}  ${pc.green("audit generated")} ${pc.dim(markdownPath)}`);
    console.log(`  ${ICON.ok}  ${pc.green("scores         ")} ${pc.dim(formatScoreLine(plan.scores))}`);
    const belowTarget = plan.self_critique.filter((item) => !item.passed);
    if (belowTarget.length === 0) {
      console.log(`  ${ICON.ok}  ${pc.green("score gate     ")} ${pc.dim("all dimensions at 95+")}`);
    } else {
      console.log(`  ${ICON.warn}  ${pc.yellow("score gate     ")} ${pc.dim(`${belowTarget.length} dimension${belowTarget.length === 1 ? "" : "s"} below 95`)}`);
    }
  }

  const approved = await requireWriteApproval(options, interactive);
  if (!approved) return;

  let targetInitiativeId = options.attachToInitiative?.trim() || "";
  if (options.createInitiative) {
    const initiative = await createInitiativeFromAuditPlan({
      dryRun: Boolean(options.dryRun),
      plan,
    });
    targetInitiativeId = initiative.id;
    console.log(`  ${ICON.ok}  ${pc.green("initiative    ")} ${pc.bold(initiative.title)} ${pc.dim(initiative.id)}`);
  }

  if (targetInitiativeId && (options.attachToInitiative || options.createInitiative)) {
    const artifact = await createAuditArtifact({
      dryRun: Boolean(options.dryRun),
      initiativeId: targetInitiativeId,
      markdown,
      plan,
    });
    console.log(`  ${ICON.ok}  ${pc.green("artifact      ")} ${pc.bold(artifact.title)} ${pc.dim(artifact.id)}`);
  }

  if (options.writeFollowUp) {
    if (!targetInitiativeId) {
      throw new Error("--write-follow-up requires --attach-to-initiative <id> or --create-initiative.");
    }
    const followUp = await createAuditFollowUpTask({
      dryRun: Boolean(options.dryRun),
      initiativeId: targetInitiativeId,
      ...(options.milestoneId?.trim() ? { milestoneId: options.milestoneId.trim() } : {}),
      plan,
      ...(options.workstreamId?.trim() ? { workstreamId: options.workstreamId.trim() } : {}),
    });
    console.log(`  ${ICON.ok}  ${pc.green("follow-up     ")} ${pc.bold(followUp.title)} ${pc.dim(followUp.id)}`);
  }
}

async function runOperatingMapCommand(
  queryParts: string[],
  options: OperatingMapCommandOptions,
): Promise<void> {
  const auth = await resolveOrgxAuth();
  if (!auth) {
    throw new Error("Operating-map discovery requires OrgX auth. Run `wizard auth login` first.");
  }

  const workspace = options.workspaceId?.trim()
    ? { id: options.workspaceId.trim(), name: options.workspaceId.trim() }
    : await getCurrentWorkspace();
  if (!workspace) {
    throw new Error("No current OrgX workspace found. Run `wizard workspace create <name>` first.");
  }

  const query = queryParts.join(" ").trim() || null;
  const mode: OperatingMapMode = options.deepSearch ? "deep_search" : "bounded_sync";
  const sourceKinds = (options.source ?? "").split(",").map((source) => source.trim()).filter(Boolean);
  const idempotencyKey = options.idempotencyKey?.trim() || defaultOperatingMapIdempotencyKey({
    workspaceId: workspace.id,
    mode,
    query,
    sourceKinds,
  });

  const spinner = createOrgxSpinner(mode === "deep_search" ? "Mapping workflows with cited deep search" : "Mapping observed workflows");
  spinner.start();
  const discovery = await startOperatingMapDiscovery({
    workspaceId: workspace.id,
    mode,
    query,
    sourceKinds,
    idempotencyKey,
  });
  spinner.succeed(discovery.duplicate ? "Replayed the existing operating-map discovery" : "Operating-map discovery complete");

  const payload = {
    workspaceId: workspace.id,
    idempotencyKey,
    duplicate: discovery.duplicate,
    run: discovery.result.run,
    processCards: discovery.result.processCards,
  };
  if (options.json) {
    console.log(JSON.stringify(payload, null, 2));
  } else {
    console.log(`  ${ICON.ok}  ${pc.green("observations  ")} ${pc.dim(String(discovery.result.run.observationCount))}`);
    console.log(`  ${ICON.ok}  ${pc.green("candidates    ")} ${pc.dim(String(discovery.result.processCards.length))}`);
    console.log(`  ${ICON.ok}  ${pc.green("citations     ")} ${pc.dim(String(discovery.result.run.citationCount))}`);
    for (const [index, card] of discovery.result.processCards.entries()) {
      console.log("");
      console.log(`  ${pc.bold(`${index + 1}. ${card.displayName}`)} ${pc.dim(`${Math.round(card.confidence * 100)}% confidence · ${card.processCandidateRef.id}`)}`);
      if (card.nextConfirmationQuestion) console.log(`     ${pc.yellow("confirm:")} ${card.nextConfirmationQuestion}`);
      if (card.riskFlags.length > 0) console.log(`     ${pc.yellow("limits:")} ${card.riskFlags.join("; ")}`);
    }
    if (discovery.result.run.limitations.length > 0) {
      console.log("");
      console.log(`  ${pc.yellow("limitations:")} ${discovery.result.run.limitations.join("; ")}`);
    }
  }

  const candidate = options.candidate?.trim();
  if (!candidate) return;
  const selected = /^\d+$/.test(candidate)
    ? discovery.result.processCards[Number(candidate) - 1]
    : discovery.result.processCards.find((card) => card.processCandidateRef.id === candidate);
  if (!selected?.processCandidateRef.id) {
    throw new Error(`Process candidate ${candidate} was not found in discovery run ${discovery.result.run.id}.`);
  }
  const interactive = Boolean(process.stdin.isTTY && process.stdout.isTTY);
  if (!options.yes) {
    if (!interactive) throw new Error("Proposing an OperatingProcess requires --yes in non-interactive mode.");
    const confirmed = await clack.confirm({
      message: `Propose “${selected.displayName}” as an OperatingProcess (still requires human confirmation)?`,
      initialValue: false,
    });
    if (clack.isCancel(confirmed) || !confirmed) return;
  }

  const proposal = await proposeOperatingProcessFromMap({
    workspaceId: workspace.id,
    discoveryRunId: discovery.result.run.id,
    processCandidateId: selected.processCandidateRef.id,
    idempotencyKey: `wizard:operating-process:${discovery.result.run.id}:${selected.processCandidateRef.id}`,
  });
  if (options.json) {
    console.log(JSON.stringify({ ...payload, proposal }, null, 2));
  } else {
    console.log(`  ${ICON.ok}  ${pc.green("proposal      ")} ${pc.dim(selected.displayName)}`);
    console.log(`  ${pc.dim("Next step     ")} Review and confirm the OperatingProcess in OrgX; the wizard never auto-activates inferred workflow ownership.`);
  }
}

function runWorkGraphExtractionSchemaCommand(options: WorkGraphExtractionSchemaCommandOptions): void {
  const protocol = buildWorkGraphExtractionProtocol();
  const outputPath = options.output?.trim() ? resolve(options.output.trim()) : "";
  if (outputPath) {
    if (options.json) {
      writeJsonFile(outputPath, protocol);
    } else {
      writeTextFile(outputPath, renderWorkGraphExtractionProtocolMarkdown(protocol));
    }
  }

  if (options.json) {
    console.log(JSON.stringify(protocol, null, 2));
    return;
  }

  const markdown = renderWorkGraphExtractionProtocolMarkdown(protocol);
  console.log(markdown);
}

function normalizeRuntimePacketSource(source: string): "codex" | "claude-code" {
  const normalized = source.trim().toLowerCase().replace(/_/g, "-");
  if (normalized === "codex") return "codex";
  if (normalized === "claude" || normalized === "claude-code" || normalized === "claude_code") return "claude-code";
  throw new Error("Unsupported runtime event source. Use codex or claude.");
}

function normalizeRuntimePacketRole(role?: string): "user" | "assistant" | "tool" | "meta" {
  const normalized = role?.trim().toLowerCase();
  if (normalized === "user" || normalized === "assistant" || normalized === "tool" || normalized === "meta") {
    return normalized;
  }
  return "assistant";
}

function runWorkGraphRuntimeEventCommand(options: WorkGraphRuntimeEventCommandOptions): void {
  const source = normalizeRuntimePacketSource(options.source);
  const cwd = resolve(options.cwd?.trim() || process.cwd());
  const outputRoot = resolve(cwd, options.outputDir?.trim() || ".orgx/work-graph/runtime-events");
  const generatedAt = new Date().toISOString();
  const timestamp = generatedAt.replace(/[:.]/g, "-");
  const summary = options.summary?.trim() || options.message?.trim();
  if (!summary) {
    throw new Error("Runtime event summary is required. Pass --summary or --message.");
  }

  const packet = {
    type: "orgx_work_graph_runtime_event",
    schema_version: "1.0.0",
    source_client: source,
    timestamp: generatedAt,
    role: normalizeRuntimePacketRole(options.role),
    event_kind: options.eventKind?.trim() || "artifact",
    summary,
    content: summary,
    ...(options.toolName?.trim() ? { tool_name: options.toolName.trim() } : {}),
    cwd,
    collection_method: "runtime_packet",
    redaction_state: "agent_redacted",
  };
  const path = resolve(outputRoot, source, `${timestamp}-${process.pid}.jsonl`);
  writeTextFile(path, `${JSON.stringify(packet)}\n`, { mode: 0o600 });

  if (options.json) {
    console.log(JSON.stringify({ ok: true, path, source, collectedBy: "work-graph profile --from all" }, null, 2));
    return;
  }
  console.log(`  ${ICON.ok}  ${pc.green("runtime event")} ${pc.dim(path)}`);
  console.log(`  ${ICON.skip}  ${pc.dim("collect with")} ${pc.bold("orgx-wizard work-graph profile --from all")}`);
}

async function runWorkGraphCommand(
  options: WorkGraphCommandOptions,
  defaults: { from?: string } = {},
): Promise<void> {
  const interactive = Boolean(process.stdin.isTTY && process.stdout.isTTY);
  const commandOptions = {
    ...options,
    ...(options.from?.trim() ? {} : defaults.from ? { from: defaults.from } : {}),
  };
  const auditInputs = await readWorkGraphInputs(commandOptions, interactive);
  const workspace = await resolveAuditWorkspace(commandOptions);
  const generatedAt = new Date().toISOString();
  const nativeClientExtractions = buildNativeAiClientExtractions(
    auditInputs.imports,
    generatedAt,
  );
  const clientExtractions = [
    ...auditInputs.clientExtractions,
    ...nativeClientExtractions,
  ];
  const report = buildSessionReconciliationReport({
    clientExtractions,
    connectedSources: [
      ...auditInputs.connectedSources,
      ...(workspace.id === "local-workspace" ? [] : ["OrgX workspace"]),
    ],
    imports: auditInputs.imports,
    investigationRawEvents: auditInputs.investigationRawEvents,
    missingSources: [
      ...(workspace.id === "local-workspace" ? ["OrgX workspace auth"] : []),
      ...auditInputs.missingSources,
    ],
    generatedAt,
    workspace,
  });
  const markdown = renderWorkGraphMarkdown(report);
  const outputDir = resolve(commandOptions.outputDir?.trim() || ".orgx/work-graph");
  const timestamp = report.generated_at.replace(/[:.]/g, "-");
  const jsonPath = resolve(outputDir, `work-graph-report-${timestamp}.json`);
  const markdownPath = resolve(outputDir, `work-graph-report-${timestamp}.md`);
  const agentBriefPath = resolve(outputDir, `work-graph-agent-brief-${timestamp}.md`);

  writeJsonFile(jsonPath, report);
  writeTextFile(markdownPath, markdown);

  let published:
    | {
        ok: boolean;
        status: number;
        publicUrl?: string;
        reviewUrl?: string;
      }
    | null = null;
  const shouldPublish = Boolean(commandOptions.publish || commandOptions.publicShare);
  if (shouldPublish) {
    const approved = await requireWorkGraphPublishApproval(commandOptions, interactive);
    if (approved) {
      if (commandOptions.dryRun) {
        published = { ok: true, status: 0 };
      } else {
        const publishResult = await publishWorkGraphReport(report, {
          ...(commandOptions.artifactUrl?.trim() ? { artifactUrl: commandOptions.artifactUrl.trim() } : {}),
          attachArtifact: Boolean(commandOptions.attachArtifact || commandOptions.attachToInitiative),
          ...(commandOptions.entityId?.trim() ? { entityId: commandOptions.entityId.trim() } : {}),
          ...(commandOptions.entityType ? { entityType: commandOptions.entityType } : {}),
          ...(commandOptions.attachToInitiative?.trim() ? { initiativeId: commandOptions.attachToInitiative.trim() } : {}),
          publicShare: Boolean(commandOptions.publicShare || commandOptions.publish),
          ...(workspace.id !== "local-workspace" ? { workspaceId: workspace.id } : {}),
        });
        if (!publishResult.ok) {
          throw new Error(`Work Graph publish failed with HTTP ${publishResult.status}: ${JSON.stringify(publishResult.data)}`);
        }
        const body = isRecord(publishResult.data) ? publishResult.data : {};
        const publicShare = isRecord(body.public_share) ? body.public_share : null;
        const publicUrl = typeof publicShare?.url === "string"
          ? `${normalizeOrgxBaseUrl(process.env.ORGX_BASE_URL || DEFAULT_ORGX_BASE_URL)}${publicShare.url}`
          : undefined;
        const readout = isRecord(body.public_readout) ? body.public_readout : null;
        published = {
          ok: true,
          status: publishResult.status,
          ...(publicUrl ? { publicUrl } : {}),
          ...(typeof readout?.review_url === "string" ? { reviewUrl: readout.review_url } : {}),
        };
      }
    }
  }

  if (published?.publicUrl || published?.reviewUrl) {
    writeTextFile(markdownPath, renderWorkGraphMarkdown(report, published));
  }
  writeTextFile(agentBriefPath, renderAqAgentBrief(report, published ?? {}));

  if (commandOptions.json) {
    console.log(JSON.stringify({
      jsonPath,
      markdownPath,
      agentBriefPath,
      reportId: report.report_id,
      workGraphFingerprint: report.work_graph_fingerprint,
      hydrationKey: report.signup_hydration.hydration_key,
      finalState: report.final_state,
      opportunityScore: report.opportunity_score,
      agenticQuotient: report.agentic_quotient,
      executionQuality: report.execution_quality,
      pageQuality: report.page_quality,
      impactProjection: report.impact_projection,
      missedOrchestration: report.missed_orchestration_opportunities.length,
      clientExtractionCount: report.client_extractions.length,
      kickoffCount: report.initiative_kickoffs.length,
      findingCount: report.trails.length,
      recurringPatternCount: report.recurring_patterns.length,
      attributionCount: report.attribution_spine.review.pending_count,
      topFinding: report.mirror.primary_trail_id ?? null,
      readoutHeadline: report.mirror.headline,
      published,
    }, null, 2));
    return;
  }

  const story = buildAqProfileStory(report);
  const aq = report.agentic_quotient;
  console.log("");
  console.log(`  ${pc.bgGreen(pc.black(` AQ ${aq.aq} `))}  ${pc.bold(story.primary)}`);
  console.log(`  ${pc.dim(story.archetype)}  ${pc.dim(`Stack ${aq.stack_score} · Durable ${aq.durability_score} · Gap ${aq.agentic_gap} → ${aq.ceiling}`)}`);
  console.log("");
  for (const [index, signal] of story.signals.entries()) {
    const marker = pc.dim(`0${index + 1}`);
    console.log(`  ${marker}  ${pc.dim(signal.eyebrow.toUpperCase())}`);
    console.log(`      ${pc.bold(signal.title)}  ${pc.dim(signal.evidence)}`);
    console.log(`      ${pc.dim(signal.detail)}`);
  }
  console.log("");
  console.log(`  ${pc.bgYellow(pc.black(" GROWTH EDGE "))}  ${pc.bold(story.growthEdge.title)}${story.growthEdge.expectedLift > 0 ? pc.yellow(`  +${story.growthEdge.expectedLift} AQ`) : ""}`);
  console.log(`      ${story.growthEdge.detail}`);
  console.log("");
  console.log(`  ${ICON.ok}  ${pc.green("receipts     ")} ${pc.dim(story.provenance)}`);
  console.log(`  ${ICON.ok}  ${pc.green("audit        ")} ${pc.dim(`${report.execution_quality.overall}/100 quality · ${report.source_coverage.coverage_score ?? 0}/100 source coverage · ${report.trails.length} evidence trails`)}`);
  console.log(`  ${ICON.ok}  ${pc.green("report       ")} ${pc.dim(markdownPath)}`);
  console.log(`  ${ICON.ok}  ${pc.green("agent brief  ")} ${pc.dim(agentBriefPath)}`);
  console.log(`  ${ICON.skip}  ${pc.dim("ask an agent ")} ${pc.dim("Explain the evidence behind my growth edge, then propose the smallest verifiable repair.")}`);
  if (published?.publicUrl) {
    console.log(`  ${ICON.ok}  ${pc.green("profile      ")} ${pc.bold(published.publicUrl)}`);
  }
}

async function printPluginStatusSection(): Promise<OrgxPluginStatus[]> {
  const spinner = createOrgxSpinner("Checking OrgX companion plugin status");
  spinner.start();
  const statuses = await listOrgxPluginStatuses();
  spinner.succeed("OrgX companion plugin status checked");
  console.log("");
  console.log(pc.dim("  plugins"));
  printPluginStatusReport(statuses);
  return statuses;
}

async function checkPluginStatusesCompact(): Promise<OrgxPluginStatus[]> {
  const spinner = createOrgxSpinner("Checking OrgX companion plugin status");
  spinner.start();
  const statuses = await listOrgxPluginStatuses();
  const installable = statuses.filter((s) => s.available && !s.installed);
  const installed = statuses.filter((s) => s.installed).length;
  if (installable.length === 0) {
    spinner.succeed(`Plugins ready (${installed} installed)`);
    return statuses;
  }
  spinner.succeed("OrgX companion plugin status checked");
  console.log("");
  console.log(pc.dim("  plugins"));
  printPluginStatusReport(statuses);
  return statuses;
}

function printSkillInstallReport(report: SkillsInstallReport): void {
  for (const write of report.writes) {
    const icon = write.skipped ? ICON.warn : write.changed ? ICON.ok : ICON.skip;
    const state = write.skipped ? pc.yellow("skipped  ") : write.changed ? pc.green("updated  ") : pc.dim("unchanged");
    console.log(`  ${icon}  ${pc.bold(write.label.padEnd(10))} ${state}`);
    if (write.reason) {
      console.log(`       ${pc.dim(write.reason)}`);
    }
  }

  for (const pack of report.packs) {
    const changedCount = pack.files.filter((f) => f.changed).length;
    const skippedCount = pack.files.filter((f) => f.skipped).length;
    const icon = skippedCount > 0 ? ICON.warn : changedCount > 0 ? ICON.ok : ICON.skip;
    const state = skippedCount > 0
      ? pc.yellow(`${skippedCount} skipped `)
      : changedCount > 0
        ? pc.green(`${changedCount} updated `)
        : pc.dim("unchanged");
    console.log(`  ${icon}  ${pc.bold(pack.name.padEnd(10))} ${state}  ${pc.dim(`${pack.files.length} files`)}`);
    for (const file of pack.files.filter((entry) => entry.skipped && entry.reason)) {
      console.log(`       ${pc.dim(`${file.label}: ${file.reason}`)}`);
    }
  }

  for (const note of report.notes) {
    console.log(`  ${ICON.skip}  ${pc.dim(note)}`);
  }
}

function countSkillReportChanges(report: SkillsInstallReport): number {
  const writeChanges = report.writes.filter((write) => write.changed && !write.skipped).length;
  const packChanges = report.packs.reduce(
    (count, pack) => count + pack.files.filter((file) => file.changed && !file.skipped).length,
    0,
  );
  return writeChanges + packChanges;
}

function printSkillExtensions(extensions: readonly SkillExtension[]): void {
  if (extensions.length === 0) {
    console.log(`  ${ICON.skip}  ${pc.dim("no skill extensions yet")}`);
    return;
  }

  for (const extension of extensions) {
    const icon = extension.enabled ? ICON.ok : ICON.skip;
    const state = extension.enabled ? pc.green("enabled  ") : pc.dim("disabled ");
    console.log(
      `  ${icon}  ${pc.bold(extension.id.padEnd(24))} ${state} ${pc.dim(extension.path)}`,
    );
  }
}

function printLocalSkillCandidates(candidates: readonly LocalSkillCandidate[]): void {
  if (candidates.length === 0) {
    console.log(`  ${ICON.skip}  ${pc.dim("no local skill files found")}`);
    return;
  }

  candidates.forEach((candidate, index) => {
    const domains = candidate.agentDomains.join(", ");
    console.log(
      `  ${ICON.ok}  ${pc.bold(String(index + 1).padStart(2, " "))} ${pc.bold(candidate.title)} ${pc.dim(candidate.id)}`,
    );
    console.log(`       ${pc.dim(`${candidate.source} · ${domains} · ${candidate.reason}`)}`);
    console.log(`       ${pc.dim(candidate.path)}`);
    if (candidate.snippet) {
      console.log(`       ${pc.dim(candidate.snippet)}`);
    }
  });
}

function writeLocalSkillSelection(input: {
  candidates: readonly LocalSkillCandidate[];
  context?: string;
  scope?: "user" | "workspace" | "project";
}): SkillExtensionWrite {
  const scope = input.scope ?? "user";
  const nextContent = buildLocalSkillExtensionContent(input.candidates, input.context);
  const existing = listSkillExtensions().find(
    (extension) => extension.skillId === "orgx" && extension.scope === scope,
  );
  const content = existing?.content.trim()
    ? `${existing.content.trimEnd()}\n\n${nextContent}`
    : nextContent;

  return addSkillExtension({
    content,
    overwrite: true,
    scope,
    skillId: "orgx",
    title: "Local skill preferences",
  });
}

function printSkillExtensionWrite(result: SkillExtensionWrite): void {
  const action = result.created ? "created" : result.changed ? "updated" : "unchanged";
  const color = result.created || result.changed ? pc.green : pc.dim;
  console.log(
    `  ${result.created || result.changed ? ICON.ok : ICON.skip}  ${pc.bold(result.extension.id.padEnd(24))} ${color(action)}`,
  );
  console.log(`       ${pc.dim(result.path)}`);
}

function openPathInEditor(path: string): boolean {
  const editor = process.env.EDITOR || process.env.VISUAL;
  if (!editor) {
    return false;
  }

  const result = spawnSync(editor, [path], {
    shell: true,
    stdio: "inherit",
  });
  return result.status === 0;
}

async function safeTrackWizardTelemetry(
  event: string,
  properties: Record<string, unknown> = {},
): Promise<void> {
  try {
    await trackWizardTelemetry(event, properties);
  } catch {
    // Telemetry should never block setup/auth flows.
  }
}

function printWorkspace(workspace: OrgxWorkspace): void {
  console.log(
    `${pc.bold(workspace.name)}${workspace.isDefault ? ` ${pc.green("(default)")}` : ""}`,
  );
  console.log(`  id: ${workspace.id}`);
  if (workspace.description) {
    console.log(`  description: ${workspace.description}`);
  }
  if (workspace.createdAt) {
    console.log(`  created: ${workspace.createdAt}`);
  }
  if (workspace.updatedAt) {
    console.log(`  updated: ${workspace.updatedAt}`);
  }
}

function printWorkspaceList(workspaces: OrgxWorkspace[]): void {
  if (workspaces.length === 0) {
    console.log(pc.yellow("No workspaces found for this OrgX user."));
    return;
  }

  for (const workspace of workspaces) {
    printWorkspace(workspace);
  }
}

function printWorkspaceSetupResult(result: WorkspaceSetupResult): void {
  if (result.workspace) {
    const icon = result.status === "updated" ? ICON.ok : ICON.skip;
    console.log(`  ${icon}  ${pc.green("workspace ")} ${pc.bold(result.workspace.name)}`);
  } else {
    console.log(`  ${ICON.skip}  ${pc.dim(result.message)}`);
  }
}

function printSetupScopeNote(): void {
  console.log(pc.dim("  setup updates your AI tools with:"));
  console.log(`  ${ICON.skip}  ${pc.dim("MCP configs for local and cloud OrgX access")}`);
  console.log(`  ${ICON.skip}  ${pc.dim("OrgX agent skills, rules, prompts, commands, and hooks")}`);
  console.log(`  ${ICON.skip}  ${pc.dim("companion plugins where your editor supports them")}`);
}

function printFirstValueHandoff(input: {
  context?: string;
  initiative: FirstValueInitiativeResult;
  profile?: SetupProfile | null;
  workspace: Pick<OrgxWorkspace, "name">;
}): void {
  const cmd = getCmd();
  const fallbackPrompt = `Use OrgX to continue "${input.initiative.initiative.title}" in ${input.workspace.name}. Show the next action, then start with the onboarding task.`;
  const handoffPrompt = buildProfileHandoffPrompt(input.profile, fallbackPrompt, input.context);
  console.log("");
  console.log(pc.bold("first OrgX handoff"));
  console.log(
    `  ${input.initiative.created ? pc.green("created") : pc.yellow("ready")} ${pc.bold(input.initiative.initiative.title)}`,
  );
  console.log(`  live: ${input.initiative.liveUrl}`);
  console.log(
    `  ${pc.dim("ask your AI tool:")} ${pc.cyan(
      handoffPrompt,
    )}`,
  );
  if (input.profile) {
    console.log(`  ${pc.dim("local proof:")} ${pc.cyan(input.profile.localProofCommand)}`);
    console.log(`  ${pc.dim("skills:")} ${pc.cyan(input.profile.skillDiscoveryCommand)}`);
  }
  console.log(`  ${pc.dim("later:")} ${pc.cyan(`${cmd} doctor`)} ${pc.dim("checks tool wiring")}`);
}

function firstValueRecordToResult(
  record: WizardFirstValueInitiativeRecord | undefined,
): FirstValueInitiativeResult | null {
  if (!record) {
    return null;
  }

  return {
    created: false,
    initiative: {
      id: record.id,
      title: record.title,
      ...(record.summary ? { summary: record.summary } : {}),
    },
    liveUrl: record.liveUrl,
  };
}

function printFounderPresetResult(result: FounderPresetResult): void {
  printMutationResults(result.surfaceResults);
  console.log("");
  printSkillInstallReport(result.skillReport);

  if (result.workspaceSetup) {
    console.log("");
    printWorkspaceSetupResult(result.workspaceSetup);
  }
}

function normalizePromptResult<T>(value: T | symbol): T | symbol {
  return value;
}

async function secretPrompt(message: string): Promise<string | null> {
  if (!process.stdin.isTTY || !process.stdout.isTTY) {
    return null;
  }

  process.stdout.write(`${message}: `);

  const stdin = process.stdin;
  const stdout = process.stdout;
  const previousRawMode = stdin.isRaw === true;

  return await new Promise<string | null>((resolve) => {
    let value = "";

    const cleanup = (result: string | null) => {
      stdin.off("data", onData);
      if (stdin.isTTY) {
        stdin.setRawMode(previousRawMode);
      }
      stdin.pause();
      stdout.write("\n");
      resolve(result);
    };

    const onData = (chunk: Buffer | string) => {
      const text = chunk.toString("utf8");

      for (const char of text) {
        if (char === "\u0003") {
          cleanup(null);
          return;
        }

        if (char === "\r" || char === "\n") {
          cleanup(value.trim());
          return;
        }

        if (char === "\u007f" || char === "\b") {
          value = value.slice(0, -1);
          continue;
        }

        if (char >= " ") {
          value += char;
        }
      }
    };

    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.on("data", onData);
  });
}

async function selectPrompt(input: {
  initialValue?: string | undefined;
  message: string;
  options: { hint?: string; label: string; value: string }[];
}): Promise<string | symbol> {
  const promptInput = input.initialValue
    ? { ...input, initialValue: input.initialValue }
    : { message: input.message, options: input.options };

  return normalizePromptResult(await clack.select<string>(promptInput));
}

async function textPrompt(input: {
  initialValue?: string | undefined;
  message: string;
  placeholder?: string;
  validate?: (value: string | undefined) => Error | string | undefined;
}): Promise<string | symbol> {
  const promptInput = input.initialValue
    ? { ...input, initialValue: input.initialValue }
    : {
        message: input.message,
        ...(input.placeholder ? { placeholder: input.placeholder } : {}),
        ...(input.validate ? { validate: input.validate } : {}),
      };

  return normalizePromptResult(await clack.text(promptInput));
}

async function multiselectPrompt(input: {
  initialValues?: string[] | undefined;
  message: string;
  options: { disabled?: boolean; hint?: string; label: string; value: string }[];
  required?: boolean;
}): Promise<string[] | symbol> {
  const promptInput = {
    message: `${input.message} ${pc.dim("Space to select, Enter to continue. Defaults are preselected.")}`,
    options: input.options,
    ...(input.initialValues ? { initialValues: input.initialValues } : {}),
    ...(input.required !== undefined ? { required: input.required } : {}),
  };

  return normalizePromptResult(await clack.multiselect<string>(promptInput));
}

function printAgentRoster(roster: WizardAgentRosterRecord): void {
  console.log(`  ${ICON.ok}  ${pc.green("agent roster")} ${pc.bold(`${roster.agents.length} agents`)}`);
  for (const agent of roster.agents) {
    console.log(
      `       ${pc.dim(agent.domain.padEnd(12))} ${pc.bold(agent.name)} ${pc.dim(agent.agentType)}`,
    );
  }
}

async function promptForGuidedAgentRoster(
  workspace: OrgxWorkspace,
): Promise<"cancelled" | WizardAgentRosterRecord | null> {
  const remainingAgents = listAgentRosterProfiles();
  const assignments: AgentRosterAssignment[] = [];

  for (const domain of AGENT_ROSTER_DOMAINS) {
    if (remainingAgents.length === 0) {
      break;
    }

    const defaultAgent = remainingAgents.find((agent) => agent.defaultDomain === domain);
    const choice = await selectPrompt({
      initialValue: defaultAgent?.name ?? "skip",
      message: `Assign an OrgX agent to the ${domain} domain`,
      options: [
        ...remainingAgents.map((agent) => ({
          value: agent.name,
          label: agent.name,
          hint: `${agent.agentType} · default ${agent.defaultDomain}`,
        })),
        { value: "skip", label: `Leave ${domain} unassigned`, hint: "optional" },
      ],
    });

    if (clack.isCancel(choice)) {
      clack.cancel("Setup cancelled.");
      return "cancelled";
    }

    if (choice === "skip") {
      continue;
    }

    const index = remainingAgents.findIndex((agent) => agent.name === choice);
    if (index === -1) {
      continue;
    }

    const [selectedAgent] = remainingAgents.splice(index, 1);
    if (!selectedAgent) {
      continue;
    }

    assignments.push({ domain, name: selectedAgent.name });
  }

  if (assignments.length === 0) {
    return null;
  }

  return configureAgentRoster(workspace, assignments);
}

async function verifyAndPersistAuth(
  apiKey: string,
  options: { baseUrl?: string; telemetrySource?: string },
): Promise<{
  verification: OrgxAuthCheckResult;
} | {
  openclawResults: SurfaceMutationResult[];
  stored: Awaited<ReturnType<typeof writeWizardAuth>>;
  verification: OrgxAuthCheckResult;
}> {
  const trimmedKey = apiKey.trim();
  if (!trimmedKey.toLowerCase().startsWith("oxk_")) {
    throw new Error("Expected a per-user OrgX API key that starts with oxk_.");
  }

  const baseUrl = normalizeOrgxBaseUrl(options.baseUrl);
  const verification = await verifyOrgxAuth({
    apiKey: trimmedKey,
    keyPrefix: trimmedKey.slice(0, 12),
    baseUrl,
    source: "wizard-store",
  });

  if (!verification.ok) {
    return { verification };
  }

  const stored = await writeWizardAuth({
    apiKey: trimmedKey,
    baseUrl,
    verifiedAt: new Date().toISOString(),
  });
  await safeTrackWizardTelemetry("auth_completed", {
    auth_source: options.telemetrySource ?? "manual_api_key",
    has_base_url: Boolean(baseUrl),
  });
  const openclawResults = detectSurface("openclaw").detected
    ? await addSurface("openclaw")
    : [];

  return { openclawResults, stored, verification };
}

async function syncContinuityAfterAuth(seed: {
  executionMode?: "cloud" | "local" | null;
  workspaceName?: string | null;
} = {}): Promise<void> {
  try {
    const workspace = await getCurrentWorkspace().catch(() => null);
    persistContinuityDefaults({
      ...(seed.executionMode !== undefined ? { executionMode: seed.executionMode } : {}),
      workspace,
      workspaceName: seed.workspaceName ?? workspace?.name ?? null,
    });
  } catch {
    // Auth success should not depend on workspace or continuity writes.
  }
}

function parseTimeoutSeconds(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error("--timeout must be a positive number of seconds.");
  }
  return parsed;
}

/**
 * Read a single keystroke from stdin in raw mode. Returns the lowercased
 * character, or null on EOF / Ctrl-C / non-TTY. Mirrors the `secretPrompt`
 * pattern used elsewhere in this file so we do not pull in an extra dep
 * for the M-P5 intent flow.
 */
async function readSingleKey(): Promise<string | null> {
  const stdin = process.stdin;
  if (!stdin.isTTY) return null;

  const previousRawMode = stdin.isRaw === true;
  return await new Promise<string | null>((resolve) => {
    const cleanup = (result: string | null) => {
      stdin.off("data", onData);
      if (stdin.isTTY) {
        stdin.setRawMode(previousRawMode);
      }
      stdin.pause();
      resolve(result);
    };

    const onData = (chunk: Buffer | string) => {
      const text = chunk.toString("utf8");
      const first = text.charAt(0);
      if (first === "\u0003" || first === "\u0004") {
        cleanup(null);
        return;
      }
      cleanup(first.toLowerCase());
    };

    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.on("data", onData);
  });
}

/**
 * Pretty-print the classified intent: routing line, plan preview, hotkey bar.
 * Stays pure on the render side (all strings built in `intent-render.ts`); the
 * only side effects are `console.log` calls.
 */
function printIntentPreview(intent: Intent, baseUrl: string): void {
  const render = detectRenderCapabilities();
  const agent: AgentSlug = intent.suggested_agent_slug ?? "xandy";
  const confidence = resolveConfidenceDecimal(intent.plan_preview);

  console.log("");
  console.log(renderRoutingLine(agent, confidence, render));
  console.log("");
  console.log(renderPlanPreview(intent.plan_preview, render));
  console.log("");
  console.log(
    renderDim(
      `  slug ${intent.slug}   handoff ${buildIntentHandoffUrl(intent.slug, { baseUrl })}`,
      render,
    ),
  );
  console.log("");
  console.log(renderHotkeyBar(DEFAULT_HOTKEYS, render));
}

/**
 * Capture a first OrgX intent during `setup`. Replaces the old people-first
 * capture: instead of asking for one contact by hand, we ask the user what
 * they want OrgX to move, hand that to the classifier, and let the agent
 * draft the ICP + source the people downstream. Mirrors the Fon fixture
 * pattern in the main orgx repo (lib/execution/fixtures/fonScenario.ts).
 *
 * Contract:
 *  - Requires an interactive TTY and a resolved workspace, otherwise skipped.
 *  - One prompt. Blank answer = skip. Non-blank = create intent + preview.
 *  - Optionally approves the intent so the /command view opens in "ready"
 *    state instead of "pending".
 *  - Never throws; network/auth failures return "failed" and print a hint.
 */
async function maybeCaptureSetupIntent(input: {
  interactive: boolean;
  workspace: OrgxWorkspace | null;
}): Promise<"skipped" | "captured" | "approved" | "failed" | "cancelled"> {
  if (!input.interactive || !input.workspace) {
    return "skipped";
  }
  if (hasSetupPromptSkip(input.workspace.id, "setup_intent")) {
    return "skipped";
  }

  const prompt = await textPrompt({
    message: "In one line, what do you want OrgX to move first? (blank to skip — you can start from the dashboard)",
    placeholder: "e.g. open a warm-intro loop to 50 design-led SaaS founders",
    validate: (value) => {
      const trimmed = (value ?? "").trim();
      if (!trimmed) return undefined;
      if (trimmed.length > 4_000) return "Keep it under 4,000 characters.";
      return undefined;
    },
  });
  if (clack.isCancel(prompt)) {
    return "cancelled";
  }
  const intentText = typeof prompt === "string" ? prompt.trim() : "";
  if (!intentText) {
    recordSetupPromptSkip({
      promptKey: "setup_intent",
      workspaceId: input.workspace.id,
      workspaceName: input.workspace.name,
    });
    return "skipped";
  }

  const spinner = createOrgxSpinner("Routing");
  spinner.start();

  let intent: Intent;
  try {
    intent = await createIntent({
      workspace_id: input.workspace.id,
      text: intentText,
    });
    spinner.stop();
  } catch (error) {
    spinner.fail("Could not classify intent.");
    const message = error instanceof Error ? error.message : String(error);
    console.log(`  ${ICON.warn}  ${pc.yellow("intent")}  ${pc.dim(message)}`);
    return "failed";
  }

  const auth = await resolveOrgxAuth().catch(() => null);
  const baseUrl = auth?.baseUrl ?? DEFAULT_ORGX_BASE_URL;

  printIntentPreview(intent, baseUrl);

  const approveChoice = await clack.confirm({
    message: "Approve this plan and hand off to OrgX?",
    initialValue: true,
  });
  if (clack.isCancel(approveChoice)) {
    return "cancelled";
  }

  if (approveChoice) {
    const approveSpinner = createOrgxSpinner("Approving");
    approveSpinner.start();
    try {
      await updateIntent(intent.id, { status: "approved" });
      approveSpinner.stop();
    } catch (error) {
      approveSpinner.fail("Approve failed.");
      const message = error instanceof Error ? error.message : String(error);
      console.log(`  ${ICON.warn}  ${pc.yellow("intent")}  ${pc.dim(message)}`);
      return "failed";
    }
    const handoff = buildIntentHandoffUrl(intent.slug, { baseUrl });
    console.log(`  ${ICON.ok}  ${pc.green("intent")}     ${pc.dim(`approved · ${handoff}`)}`);
    return "approved";
  }

  const handoff = buildIntentHandoffUrl(intent.slug, { baseUrl });
  console.log(`  ${ICON.skip}  ${pc.dim(`intent pending · ${handoff}`)}`);
  return "captured";
}

async function runIntentCommand(options: {
  initialText?: string;
  workspaceId?: string;
  jsonOutput?: boolean;
}): Promise<void> {
  const interactive = isInteractive();

  // Step 1 — collect the intent text. In an interactive TTY we use clack's
  // text prompt (same styling as the rest of the wizard); non-TTY invocations
  // must pass the intent as arguments so the command stays pipeable.
  let intentText = options.initialText?.trim() ?? "";
  if (!intentText) {
    if (!interactive) {
      console.error(
        pc.red("Intent text is required when running non-interactively."),
      );
      console.error(
        pc.dim("  Example: orgx-wizard intent \"launch partner outreach campaign\""),
      );
      process.exitCode = 1;
      return;
    }
    const prompt = await textPrompt({
      message: "What do you want to move?",
      placeholder: "e.g. launch partner outreach campaign",
      validate: (value) => {
        const trimmed = (value ?? "").trim();
        if (!trimmed) return "Intent text cannot be empty.";
        if (trimmed.length > 4_000) return "Intent text exceeds 4,000 characters.";
        return undefined;
      },
    });
    if (clack.isCancel(prompt) || typeof prompt !== "string") {
      console.log(pc.dim("Cancelled."));
      return;
    }
    intentText = prompt.trim();
  }

  // Step 2 — resolve the workspace. The intent API insists on a workspace id;
  // we default to the current workspace so the happy path is `orgx-wizard
  // intent "..."` with no extra flags.
  let workspaceId = options.workspaceId ?? "";
  let workspaceName: string | null = null;
  let workspaceBaseUrl: string | null = null;
  if (!workspaceId) {
    const loadLabel = "Loading current OrgX workspace";
    const spinner = interactive ? createOrgxSpinner(loadLabel) : null;
    if (spinner) spinner.start();
    else console.log(pc.dim(`${loadLabel}...`));
    try {
      const current = await getCurrentWorkspace();
      if (!current) {
        if (spinner) spinner.fail("No OrgX workspace is configured.");
        else console.error(pc.red("No OrgX workspace is configured."));
        console.log(
          pc.dim("  Run `orgx-wizard workspace create <name>` or pass --workspace <id>."),
        );
        process.exitCode = 1;
        return;
      }
      workspaceId = current.id;
      workspaceName = current.name;
      if (spinner) spinner.stop();
    } catch (error) {
      if (spinner) spinner.fail("Failed to load current workspace.");
      console.error(
        pc.red(error instanceof Error ? error.message : String(error)),
      );
      process.exitCode = 1;
      return;
    }
  }

  // Step 3 — classify. The spinner in interactive mode shows the spec's
  // "⠋ Classifying intent…" affordance; non-interactive runs print a single
  // status line so CI logs stay readable.
  const classifyLabel = "Routing";
  const classifySpinner = interactive
    ? createOrgxSpinner(classifyLabel)
    : null;
  if (classifySpinner) classifySpinner.start();
  else console.log(pc.dim(`${classifyLabel}...`));

  let intent: Intent;
  try {
    intent = await createIntent({ workspace_id: workspaceId, text: intentText });
    if (classifySpinner) classifySpinner.stop();
  } catch (error) {
    if (classifySpinner) classifySpinner.fail("Classification failed.");
    console.error(
      pc.red(error instanceof Error ? error.message : String(error)),
    );
    process.exitCode = 1;
    return;
  }

  // Resolve the OrgX base URL for handoff links. We prefer the auth-resolved
  // URL so environment overrides (ORGX_BASE_URL, wizard auth store) take
  // precedence over the public default.
  const auth = await resolveOrgxAuth().catch(() => null);
  const baseUrl = auth?.baseUrl ?? DEFAULT_ORGX_BASE_URL;
  workspaceBaseUrl = baseUrl;

  if (options.jsonOutput) {
    const payload = {
      intent,
      handoff_url: buildIntentHandoffUrl(intent.slug, { baseUrl }),
      workspace: workspaceName ? { id: workspaceId, name: workspaceName } : { id: workspaceId },
    };
    console.log(JSON.stringify(payload, null, 2));
    return;
  }

  // Step 4 — render the preview block.
  printIntentPreview(intent, baseUrl);
  console.log("");

  // Step 5 — hotkey bar. In non-interactive mode we exit after rendering so
  // scripts can parse stdout; interactive mode waits for a single keypress.
  if (!interactive) {
    console.log(
      pc.dim(
        `Run \`orgx-wizard intent --workspace ${workspaceId}\` interactively to approve, edit, or archive.`,
      ),
    );
    return;
  }

  let action: IntentAction | null = null;
  while (action === null) {
    const key = await readSingleKey();
    if (!key) {
      action = "quit";
      break;
    }
    action = normalizeHotkey(key);
    if (action === null) {
      console.log(
        pc.dim(`  Unrecognized key. Press a, e, x, or q.`),
      );
    }
  }

  switch (action) {
    case "approve": {
      const approveLabel = "Approving";
      const spinner = interactive ? createOrgxSpinner(approveLabel) : null;
      if (spinner) spinner.start();
      try {
        await updateIntent(intent.id, { status: "approved" });
        if (spinner) spinner.stop();
      } catch (error) {
        if (spinner) spinner.fail("Approve failed.");
        console.error(
          pc.red(error instanceof Error ? error.message : String(error)),
        );
        process.exitCode = 1;
        return;
      }
      const handoff = buildIntentHandoffUrl(intent.slug, { baseUrl: workspaceBaseUrl ?? baseUrl });
      console.log("");
      console.log(`${pc.green("✓")} Approved · ${handoff}`);
      return;
    }
    case "edit": {
      const handoff = buildIntentHandoffUrl(intent.slug, { baseUrl: workspaceBaseUrl ?? baseUrl });
      console.log("");
      console.log(`${pc.yellow("✎")} Opening ${handoff}`);
      const result = openBrowser(handoff);
      if (!result.ok) {
        console.log(pc.dim(`  Could not launch browser: ${result.error}`));
        console.log(pc.dim(`  Visit ${handoff} to finish editing.`));
      }
      return;
    }
    case "archive": {
      const archiveLabel = "Archiving";
      const spinner = interactive ? createOrgxSpinner(archiveLabel) : null;
      if (spinner) spinner.start();
      try {
        await updateIntent(intent.id, { status: "archived" });
        if (spinner) spinner.stop();
      } catch (error) {
        if (spinner) spinner.fail("Archive failed.");
        console.error(
          pc.red(error instanceof Error ? error.message : String(error)),
        );
        process.exitCode = 1;
        return;
      }
      console.log("");
      console.log(`${pc.red("✕")} Archived · intent will not be executed.`);
      return;
    }
    case "quit":
    default: {
      console.log("");
      console.log(pc.dim("  Exited without saving. Intent remains pending."));
      console.log(
        pc.dim(
          `  Resume at ${buildIntentHandoffUrl(intent.slug, { baseUrl: workspaceBaseUrl ?? baseUrl })}`,
        ),
      );
      return;
    }
  }
}

async function maybeConfigureOptionalWorkspaceAddOns(input: {
  context?: string;
  initiativeId?: string;
  interactive: boolean;
  profile?: SetupProfile | null;
  telemetry?: {
    command: string;
    preset?: string;
  };
  workspace: OrgxWorkspace | null;
}): Promise<"cancelled" | "configured" | "skipped"> {
  if (!input.interactive || !input.workspace) {
    return "skipped";
  }

  const existingState = readWizardState();
  const providedInitiativeId = input.initiativeId?.trim();
  const storedFirstValueInitiative =
    existingState?.firstValueInitiative?.workspaceId === input.workspace.id
      ? existingState.firstValueInitiative
      : undefined;
  let effectiveInitiativeId = providedInitiativeId
    || storedFirstValueInitiative?.id;
  let firstValueInitiative = providedInitiativeId
    ? null
    : firstValueRecordToResult(storedFirstValueInitiative);
  const firstInitiativeSkipped = hasSetupPromptSkip(
    input.workspace.id,
    "first_initiative",
  );

  if (!effectiveInitiativeId && !firstInitiativeSkipped) {
    const firstInitiativeChoice = await selectPrompt({
      initialValue: "yes",
      message: `Create your first OrgX initiative in ${input.workspace.name}?`,
      options: [
        {
          value: "yes",
          label: "Create first initiative",
          hint: "recommended; gives setup a live next step",
        },
        { value: "no", label: "Skip initiative creation" },
      ],
    });
    if (clack.isCancel(firstInitiativeChoice)) {
      clack.cancel("Setup cancelled.");
      return "cancelled";
    }

    if (firstInitiativeChoice === "yes") {
      const title = await textPrompt({
        initialValue: input.profile?.firstInitiativeTitle ?? FIRST_VALUE_INITIATIVE_TITLE,
        message: "What should OrgX help you move forward first?",
        validate: (value) => {
          if (!value || value.trim().length === 0) {
            return "Enter an initiative title.";
          }
          return undefined;
        },
      });
      if (clack.isCancel(title)) {
        clack.cancel("Setup cancelled.");
        return "cancelled";
      }

      const spinner = createOrgxSpinner("Creating your first OrgX initiative and live handoff");
      spinner.start();
      try {
        firstValueInitiative = await ensureFirstValueInitiative(input.workspace, {
          ...(input.context || input.profile
            ? { summary: input.profile ? buildProfileSummary(input.profile, input.context) : input.context }
            : {}),
          title: String(title),
        });
        spinner.succeed(
          firstValueInitiative.created
            ? "First OrgX initiative created"
            : "First OrgX initiative ready",
        );
        effectiveInitiativeId = firstValueInitiative.initiative.id;
        await safeTrackWizardTelemetry(
          "first_value_initiative_ready",
          buildFirstValueInitiativeTelemetryProperties(
            firstValueInitiative,
            {
              command: input.telemetry?.command ?? "setup",
              ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
            },
          ),
        );
      } catch (error) {
        spinner.fail("First OrgX initiative was not created");
        const message = error instanceof Error ? error.message : String(error);
        console.log(`  ${ICON.warn}  ${pc.yellow("initiative")} ${pc.dim(message)}`);
      }
    } else if (firstInitiativeChoice === "no") {
      recordSetupPromptSkip({
        promptKey: "first_initiative",
        workspaceId: input.workspace.id,
        workspaceName: input.workspace.name,
      });
    }
  }

  const hasOnboardingTask = existingState?.onboardingTask?.workspaceId === input.workspace.id;
  const onboardingTaskSkipped = hasSetupPromptSkip(
    input.workspace.id,
    "onboarding_task",
  );
  if (!hasOnboardingTask && effectiveInitiativeId && !onboardingTaskSkipped) {
    let shouldCreateOnboardingTask = firstValueInitiative !== null;
    if (!shouldCreateOnboardingTask) {
      const onboardingChoice = await selectPrompt({
        initialValue: "yes",
        message: `Create a starter onboarding task for ${input.workspace.name}?`,
        options: [
          { value: "yes", label: "Create onboarding task", hint: "recommended" },
          { value: "no", label: "Skip task creation" },
        ],
      });
      if (clack.isCancel(onboardingChoice)) {
        clack.cancel("Setup cancelled.");
        return "cancelled";
      }
      shouldCreateOnboardingTask = onboardingChoice === "yes";
      if (onboardingChoice === "no") {
        recordSetupPromptSkip({
          promptKey: "onboarding_task",
          workspaceId: input.workspace.id,
          workspaceName: input.workspace.name,
        });
      }
    }

    if (shouldCreateOnboardingTask) {
      const spinner = createOrgxSpinner("Creating onboarding workstream and starter task");
      spinner.start();
      try {
        const task = await ensureOnboardingTask(input.workspace, {
          initiativeId: effectiveInitiativeId,
        });
        spinner.succeed("Onboarding workstream and starter task ready");
        console.log(`  ${ICON.ok}  ${pc.green("onboarding")} ${pc.bold(task.title)}`);
        await safeTrackWizardTelemetry(
          "onboarding_task_created",
          buildOnboardingTaskTelemetryProperties(
            {
              created: true,
              hasInitiative: true,
              task,
            },
            {
              command: input.telemetry?.command ?? "setup",
              ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
            },
          ),
        );
      } catch (error) {
        spinner.fail("Onboarding task was not created");
        const message = error instanceof Error ? error.message : String(error);
        console.log(`  ${ICON.warn}  ${pc.yellow("onboarding")} ${pc.dim(message)}`);
      }
    }
  }

  if (firstValueInitiative) {
    printFirstValueHandoff({
      ...(input.context ? { context: input.context } : {}),
      initiative: firstValueInitiative,
      ...(input.profile !== undefined ? { profile: input.profile } : {}),
      workspace: input.workspace,
    });
  }

  const refreshedState = readWizardState();
  const hasAgentRoster = refreshedState?.agentRoster?.workspaceId === input.workspace.id;
  const agentRosterSkipped = hasSetupPromptSkip(input.workspace.id, "agent_roster");
  if (!hasAgentRoster && !agentRosterSkipped) {
    const rosterChoice = await selectPrompt({
      initialValue: "default",
      message: `Set up an OrgX agent roster for ${input.workspace.name}?`,
      options: [
        { value: "default", label: "Install the full default roster", hint: "recommended" },
        { value: "guided", label: "Choose agents and domains" },
        { value: "no", label: "Skip roster setup" },
      ],
    });
    if (clack.isCancel(rosterChoice)) {
      clack.cancel("Setup cancelled.");
      return "cancelled";
    }
    if (rosterChoice === "guided") {
      const roster = await promptForGuidedAgentRoster(input.workspace);
      if (roster === "cancelled") {
        return "cancelled";
      }
      if (roster) {
        printAgentRoster(roster);
        await safeTrackWizardTelemetry(
          "agent_roster_configured",
          buildAgentRosterTelemetryProperties(
            {
              roster,
              strategy: "guided",
            },
            {
              command: input.telemetry?.command ?? "setup",
              ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
            },
          ),
        );
      } else {
        console.log(`  ${ICON.skip}  ${pc.dim("agent roster skipped")} ${pc.dim("(no agents selected)")}`);
        recordSetupPromptSkip({
          promptKey: "agent_roster",
          workspaceId: input.workspace.id,
          workspaceName: input.workspace.name,
        });
      }
    } else if (rosterChoice === "default") {
      const roster = ensureDefaultAgentRoster(input.workspace);
      printAgentRoster(roster);
      await safeTrackWizardTelemetry(
        "agent_roster_configured",
        buildAgentRosterTelemetryProperties(
          {
            roster,
            strategy: "default",
          },
          {
            command: input.telemetry?.command ?? "setup",
            ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
          },
        ),
      );
    } else if (rosterChoice === "no") {
      recordSetupPromptSkip({
        promptKey: "agent_roster",
        workspaceId: input.workspace.id,
        workspaceName: input.workspace.name,
      });
    }
  }

  const localSkillDiscoverySkipped = hasSetupPromptSkip(
    input.workspace.id,
    "local_skill_discovery",
  );
  if (!localSkillDiscoverySkipped) {
    const context = input.context?.trim();
    const candidates = discoverLocalSkills({
      ...(context ? { context } : {}),
      limit: 8,
    });

    if (candidates.length > 0) {
      const selectedSkillIds = await multiselectPrompt({
        message: "Bring existing local skills into your OrgX agents?",
        options: [
          ...candidates.map((candidate, index) => ({
            value: candidate.id,
            label: `${index + 1}. ${candidate.title}`,
            hint: `${candidate.source} · ${candidate.agentDomains.join(", ")} · ${candidate.reason}`,
          })),
          {
            value: "__skip__",
            label: "Skip local skill import",
            hint: "You can run `orgx-wizard skills discover-local` later.",
          },
        ],
        required: false,
      });
      if (clack.isCancel(selectedSkillIds)) {
        clack.cancel("Setup cancelled.");
        return "cancelled";
      }

      const selected = candidates.filter((candidate) => selectedSkillIds.includes(candidate.id));
      if (selectedSkillIds.includes("__skip__") || selected.length === 0) {
        recordSetupPromptSkip({
          promptKey: "local_skill_discovery",
          workspaceId: input.workspace.id,
          workspaceName: input.workspace.name,
        });
        console.log(`  ${ICON.skip}  ${pc.dim("local skills skipped")}`);
      } else {
        const result = writeLocalSkillSelection({
          candidates: selected,
          ...(context ? { context } : {}),
        });
        printSkillExtensionWrite(result);
        console.log(
          `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply selected local preferences to configured tools.`)}`,
        );
        await safeTrackWizardTelemetry("local_skills_configured", {
          candidate_count: candidates.length,
          command: input.telemetry?.command ?? "setup",
          selected_count: selected.length,
          ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
          profile: input.profile?.id ?? "none",
        });
      }
    }
  }

  return "configured";
}

async function promptOptionalCompanionPluginTargets(input: {
  interactive: boolean;
  statuses?: OrgxPluginStatus[];
}): Promise<"cancelled" | OrgxPluginStatus["target"][]> {
  if (!input.interactive) {
    return [];
  }

  let statuses = input.statuses;
  if (!statuses) {
    const spinner = createOrgxSpinner("Checking optional OrgX companion plugin status");
    spinner.start();
    statuses = await listOrgxPluginStatuses();
    spinner.succeed("Optional OrgX companion plugin status checked");
  }
  const installable = statuses.filter((status) => status.available && !status.installed);
  if (installable.length === 0) {
    return [];
  }

  const selection = await multiselectPrompt({
    initialValues: installable.map((status) => status.target),
    message: "Install OrgX companion plugins into your detected AI tools?",
    options: installable.map((status) => ({
      value: status.target,
      label: `Install ${formatPluginTargetLabel(status.target)}`,
      hint: status.message,
    })),
    required: false,
  });

  if (clack.isCancel(selection)) {
    clack.cancel("Setup cancelled.");
    return "cancelled";
  }

  return selection as OrgxPluginStatus["target"][];
}

function printPluginSkillOwnershipNote(targets: readonly OrgxPluginStatus["target"][]): void {
  if (targets.includes("claude")) {
    console.log(
      `  ${ICON.skip}  ${pc.dim(
        "The Claude Code plugin carries static OrgX skills and commands plus a focused, non-destructive, closed-world OrgX status profile through native OAuth. It installs no runtime hooks or transcript/context sync code.",
      )}`,
    );
  }

  if (targets.some((target) => target === "cursor" || target === "codex")) {
    console.log(
      `  ${ICON.skip}  ${pc.dim(
        "Cursor and Codex companion plugins carry their own OrgX assets. Use 'wizard skills add' only for standalone rules or skills when those plugins are not in play.",
      )}`,
    );
  }

  if (targets.includes("opencode")) {
    console.log(
      `  ${ICON.skip}  ${pc.dim(
        "OpenCode loads the OrgX package from its plugin config. The Wizard does not copy an API key or workspace ID into opencode.json.",
      )}`,
    );
  }
}

async function installSelectedCompanionPlugins(input: {
  targets: readonly OrgxPluginStatus["target"][];
  telemetry?: {
    command: string;
    preset?: string;
  };
}): Promise<"configured" | "skipped"> {
  if (input.targets.length === 0) {
    return "skipped";
  }

  const spinner = createOrgxSpinner("Installing OrgX companion plugins");
  spinner.start();
  const report = await installOrgxPlugins({ targets: input.targets });
  spinner.succeed("OrgX companion plugins processed");
  printPluginMutationReport(report);
  printPluginSkillOwnershipNote(input.targets);

  await safeTrackWizardTelemetry("plugins_installed", {
    changed_count: countPluginReportChanges(report),
    command: input.telemetry?.command ?? "setup",
    ...(input.telemetry?.preset ? { preset: input.telemetry.preset } : {}),
    requested_target_count: input.targets.length,
    target_count: report.results.length,
  });

  return countPluginReportChanges(report) > 0 ? "configured" : "skipped";
}

async function maybeInstallOptionalCompanionPlugins(input: {
  interactive: boolean;
  statuses?: OrgxPluginStatus[];
  telemetry?: {
    command: string;
    preset?: string;
  };
}): Promise<"cancelled" | "configured" | "skipped"> {
  const selection = await promptOptionalCompanionPluginTargets({
    interactive: input.interactive,
    ...(input.statuses ? { statuses: input.statuses } : {}),
  });
  if (selection === "cancelled") {
    return "cancelled";
  }

  return await installSelectedCompanionPlugins({
    targets: selection,
    ...(input.telemetry ? { telemetry: input.telemetry } : {}),
  });
}


function printAuthStatus(status: OrgxAuthCheckResult): void {
  if (!status.configured) {
    console.log(`  ${ICON.warn}  ${pc.yellow("not paired")}  run ${pc.cyan(`${getCmd()} auth login`)} to pair this terminal`);
    return;
  }

  const icon = status.ok ? ICON.ok : ICON.err;
  const state = status.ok ? pc.green("paired    ") : status.skipped ? pc.yellow("skipped   ") : pc.red("invalid   ");
  const via = pc.dim(formatAuthSource(status.source));
  console.log(`  ${icon}  ${state} ${pc.bold(status.keyPrefix ?? "unknown")}  ${via}`);
  if (!status.ok && status.error) {
    console.log(`       ${pc.red(status.error)}`);
  }
}

function printDoctorReport(
  report: Awaited<ReturnType<typeof runDoctor>>,
  assessment: ReturnType<typeof assessDoctorReport>,
): void {
  const verification = summarizeSetupVerification(assessment, report);
  // — Surfaces —
  console.log(pc.dim("  surfaces"));
  printSurfaceTable(report.surfaces);

  // — Account —
  console.log("");
  console.log(pc.dim("  account"));
  printAuthStatus(report.auth);

  // — Services —
  console.log("");
  console.log(pc.dim("  services"));

  // Hosted MCP
  const mcp = report.hostedMcp;
  {
    const icon = mcp.skipped ? ICON.skip : mcp.ok ? ICON.ok : ICON.err;
    const label = pc.dim("cloud mcp ".padEnd(14));
    const state = mcp.skipped ? pc.dim("skipped") : mcp.ok ? pc.green("reachable") : pc.red("unreachable");
    const tail = !mcp.ok && !mcp.skipped && mcp.error ? `  ${pc.dim(mcp.error)}` : "";
    console.log(`  ${icon}  ${label} ${state}${tail}`);
  }

  // Tool call
  const tool = report.hostedMcpTool;
  {
    const icon = tool.skipped ? ICON.skip : tool.ok ? ICON.ok : ICON.err;
    const label = pc.dim("tool call ".padEnd(14));
    const state = tool.skipped ? pc.dim("skipped") : tool.ok ? pc.green("authenticated") : pc.red("failed");
    const tail = !tool.ok && !tool.skipped && tool.error ? `  ${pc.dim(tool.error)}` : "";
    console.log(`  ${icon}  ${label} ${state}${tail}`);
  }

  // Workspace
  const ws = report.workspace;
  {
    const icon = !ws.configured ? ICON.skip : ws.ok ? ICON.ok : ICON.err;
    const label = pc.dim("workspace ".padEnd(14));
    const state = !ws.configured
      ? pc.dim("not configured")
      : ws.ok && ws.workspace
        ? pc.green(ws.workspace.name)
        : pc.red(`unreachable${ws.error ? `: ${ws.error}` : ""}`);
    console.log(`  ${icon}  ${label} ${state}`);
  }

  // OpenClaw
  const openclaw = report.openclaw;
  {
    const icon = openclaw.skipped ? ICON.skip : openclaw.ok ? ICON.ok : ICON.err;
    const label = pc.dim("openclaw ".padEnd(14));
    const state = openclaw.skipped
      ? pc.dim("not installed")
      : openclaw.ok
        ? pc.green(`healthy${openclaw.method && openclaw.method !== "none" ? ` (${openclaw.method})` : ""}`)
        : pc.red(`unhealthy${openclaw.error ? `: ${openclaw.error}` : ""}`);
    console.log(`  ${icon}  ${label} ${state}`);
  }

  // — Summary —
  console.log("");
  const configuredCount = report.surfaces.filter((s) => s.configured).length;
  if (assessment.issues.length === 0) {
    if (!report.auth.configured) {
      // Nothing is broken, but an unpaired machine isn't "ready" either — it
      // hasn't been set up yet. Don't print a green all-clear; point at setup.
      console.log(`  ${ICON.warn}  ${pc.yellow("Not set up yet — pair this terminal to finish.")}`);
      console.log(`\n  ${pc.dim("→")}  ${pc.cyan(`${getCmd()} setup`)}  ${pc.dim("configures your AI tools and pairs your account")}`);
    } else {
      console.log(`  ${ICON.ok}  ${pc.green("All systems ready.")}`);
      console.log(`  ${pc.dim("→")}  ${pc.dim(`OrgX is active across ${configuredCount} editor${configuredCount !== 1 ? "s" : ""}`)}`);
    }
  } else if (verification.transientTimeouts) {
    // Only auth/workspace timeouts — collapse to a single soft hint instead of
    // surfacing two scary red errors that suggest the user's setup is broken.
    console.log(`  ${ICON.warn}  ${pc.yellow(getSetupVerificationHeadline(verification))}`);
    console.log(`  ${pc.dim("→")}  ${pc.cyan(`${getCmd()} doctor`)}  ${pc.dim("re-runs the health checks")}`);
  } else {
    const headlineText = getSetupVerificationHeadline(verification);
    const headline = verification.status === "error"
      ? `${ICON.err}  ${pc.red(headlineText)}`
      : `${ICON.warn}  ${pc.yellow(headlineText)}`;
    console.log(`  ${headline}`);
    for (const issue of assessment.issues) {
      const degradedHostedMcpIssue =
        verification.hostedMcpDegraded && issue.title === HOSTED_MCP_OUTAGE_TITLE;
      const label = degradedHostedMcpIssue
        ? pc.yellow("degr ")
        : issue.level === "error"
          ? pc.red("error")
          : pc.yellow("warn ");
      console.log(`\n       ${label}  ${issue.title}`);
      console.log(`       ${pc.dim("→")}     ${issue.suggestion}`);
    }
  }
}

function workloadModeLabel(mode: WorkloadDiagnosisResponse["recommendation"]["mode"]): string {
  switch (mode) {
    case "none":
      return "keep local";
    case "receipt_only":
      return "portable receipts";
    case "governed_workspace":
      return "governed workspace";
  }
}

function printWorkloadDiagnosis(diagnosis: WorkloadDiagnosisResponse): void {
  const { recommendation } = diagnosis;
  console.log("");
  console.log(pc.dim("  workload"));

  const icon = recommendation.mode === "none" ? ICON.ok : ICON.warn;
  const label = recommendation.mode === "none" ? pc.green : pc.yellow;
  console.log(`  ${icon}  ${label(workloadModeLabel(recommendation.mode))}  ${pc.dim(diagnosis.diagnosis_id)}`);
  console.log(`       ${recommendation.summary}`);

  if (recommendation.rationale.length > 0) {
    console.log("");
    console.log(pc.dim("  active boundaries"));
    for (const rationale of recommendation.rationale) {
      console.log(`  ${ICON.skip}  ${rationale}`);
    }
  }

  if (diagnosis.human_approvals.length > 0) {
    console.log("");
    console.log(pc.dim("  human approvals"));
    for (const approval of diagnosis.human_approvals) {
      console.log(`  ${ICON.warn}  ${approval.decision}`);
      console.log(`       ${pc.dim(`${approval.owner_role} · ${approval.timing} · ${approval.scope.join(", ")}`)}`);
    }
  }

  console.log("");
  if (diagnosis.approval_handoff.url) {
    console.log(`  ${pc.dim("→")}  ${pc.cyan(diagnosis.approval_handoff.url)}  ${pc.dim("review before installation or access grants")}`);
  } else {
    console.log(`  ${ICON.ok}  ${pc.dim("No installation or permission handoff is warranted for this workload.")}`);
  }
  console.log(`  ${ICON.skip}  ${pc.dim("Doctor created no OrgX records, requested no credentials, and granted no authority.")}`);
}

async function main(): Promise<void> {
  initializeWizardSentry();
  const program = new Command();
  program
    .name("orgx-wizard")
    .description("Add OrgX MCP configs, skills/rules, and companion plugins to your local AI tools.")
    .showHelpAfterError();

  const pkgVersion = typeof __PKG_VERSION__ !== "undefined" ? __PKG_VERSION__ : undefined;
  program.version(pkgVersion ?? "unknown", "-V, --version");

  program.hook("preAction", (_thisCommand, actionCommand) => {
    if (Boolean(actionCommand.optsWithGlobals().json)) return;
    console.log(renderBanner(pkgVersion));
  });

  program
    .command("setup")
    .description("Add OrgX MCP configs, skills/rules, and companion plugins to detected tools.")
    .option("--preset <name>", "run a setup bundle (currently: founder)")
    .option("--profile <name>", `tailor setup for a workflow profile (${supportedSetupProfileIds().join(", ")})`)
    .option("--context <text>", "extra workflow context to include in the first initiative and handoff")
    .option("--workspace", "choose or change the default workspace during setup")
    .option("--daily-brief", "configure Daily Brief even if setup already handled it")
    .option("--skip-daily-brief", "skip Daily Brief prompts and remember the skip")
    .action(async (options: {
      context?: string;
      dailyBrief?: boolean;
      profile?: string;
      preset?: string;
      skipDailyBrief?: boolean;
      workspace?: boolean;
    }) => {
      const interactive = Boolean(process.stdin.isTTY && process.stdout.isTTY);
      if (options.dailyBrief && options.skipDailyBrief) {
        throw new Error("Use either --daily-brief or --skip-daily-brief, not both.");
      }
      const setupProfile = resolveSetupProfile(options.profile);
      if (options.profile && !setupProfile) {
        throw new Error(`Unknown setup profile '${options.profile}'. Supported profiles: ${supportedSetupProfileIds().join(", ")}.`);
      }
      const setupContext = options.context?.trim() || undefined;
      await safeTrackWizardTelemetry("wizard_started", {
        command: "setup",
        interactive,
        profile: setupProfile?.id ?? "none",
        preset: options.preset ?? "standard",
      });
      printSetupScopeNote();
      console.log("");

      if (options.preset) {
        if (options.preset !== "founder") {
          throw new Error(`Unknown setup preset '${options.preset}'. Supported presets: founder.`);
        }

        const founderPluginTargets = await promptOptionalCompanionPluginTargets({
          interactive,
        });
        if (founderPluginTargets === "cancelled") {
          return;
        }

        const spinner = createOrgxSpinner("Running founder setup preset");
        spinner.start();
        const presetResult = await runFounderPreset(
          {
            cancel: clack.cancel,
            isCancel: clack.isCancel,
            select: selectPrompt,
            text: textPrompt,
          },
          {
            interactive,
            pluginTargets: founderPluginTargets,
          },
        );

        if ("status" in presetResult) {
          spinner.stop();
          if (presetResult.status === "cancelled") {
            return;
          }
          printWorkspaceSetupResult(presetResult);
          return;
        }

        spinner.succeed("Founder setup preset complete");
        printFounderPresetResult(presetResult);
        await safeTrackWizardTelemetry("mcp_injected", {
          changed_count: presetResult.surfaceResults.filter((result) => result.changed).length,
          preset: "founder",
          surface_count: presetResult.surfaceResults.length,
        });
        await safeTrackWizardTelemetry("skills_installed", {
          changed_count: countSkillReportChanges(presetResult.skillReport),
          pack_count: presetResult.skillReport.packs.length,
          preset: "founder",
          write_count: presetResult.skillReport.writes.length,
        });
        if (presetResult.workspaceSetup) {
          await safeTrackWizardTelemetry(
            "workspace_bootstrapped",
            buildWorkspaceSetupTelemetryProperties(presetResult.workspaceSetup, {
              command: "setup",
              interactive,
              preset: "founder",
            }),
          );
        }
        const addOnResult = await maybeConfigureOptionalWorkspaceAddOns({
          ...(setupContext ? { context: setupContext } : {}),
          interactive,
          profile: setupProfile,
          telemetry: { command: "setup", preset: "founder" },
          workspace: presetResult.workspace,
        });
        if (addOnResult === "cancelled") {
          return;
        }

        await installSelectedCompanionPlugins({
          telemetry: { command: "setup", preset: "founder" },
          targets: founderPluginTargets,
        });

        console.log("");
        const doctor = await runDoctor();
        const assessment = assessDoctorReport(doctor);
        const verification = summarizeSetupVerification(assessment, doctor);
        await safeTrackWizardTelemetry(
          "setup_verified",
          buildDoctorTelemetryProperties(doctor, assessment, verification, {
            command: "setup",
            preset: "founder",
          }),
        );
        printDoctorReport(doctor, assessment);
        if (verification.status === "error") {
          process.exitCode = 1;
        }
        return;
      }

      const spinner = createOrgxSpinner("Configuring detected OrgX surfaces");
      spinner.start();
      const results = await setupDetectedSurfaces();
      spinner.succeed("Detected surfaces configured");
      printSurfaceSummary(results);
      const pluginStatuses = await checkPluginStatusesCompact();
      await safeTrackWizardTelemetry("mcp_injected", {
        changed_count: results.filter((result) => result.changed).length,
        preset: "standard",
        surface_count: results.length,
      });

      const wasAlreadyPaired = (await resolveOrgxAuth()) !== null;
      let resolvedAuth = await resolveOrgxAuth();
      let cachedWorkspaceCheck: WorkspaceConnectivityCheck | undefined;
      if (!resolvedAuth) {
        console.log("");
        if (interactive) {
          const choice = await selectPrompt({
            message: "Pair this terminal with your OrgX account to finish setup",
            options: [
              { value: "login", label: "Open browser to pair", hint: "recommended" },
              { value: "skip", label: "Skip for now", hint: `run \`${getCmd()} auth login\` later` },
            ],
          });
          if (clack.isCancel(choice)) {
            clack.cancel("Setup cancelled.");
            return;
          }
          if (choice === "login") {
            const loginOk = await runBrowserLogin({ telemetrySource: "browser_pairing" });
            if (!loginOk) {
              // Pairing failed — show clean next steps, don't dump the full doctor
              console.log(`\n  ${pc.dim("→")}  ${pc.cyan(`${getCmd()} auth login`)}           ${pc.dim("to start a fresh browser pairing")}`);
              console.log(`  ${pc.dim("→")}  ${pc.cyan(`${getCmd()} auth login --api-key`)}  ${pc.dim("to paste a key directly")}`);
              return;
            }
            resolvedAuth = await resolveOrgxAuth();
          } else {
            // User skipped
            console.log(`\n  ${pc.dim("→")}  ${pc.cyan(`${getCmd()} auth login`)}  ${pc.dim("to pair when ready")}`);
            return;
          }
        } else {
          console.log(
            `  ${ICON.warn}  ${pc.yellow("Terminal not paired.")}  Run ${pc.cyan(`${getCmd()} auth login`)} to pair it.`,
          );
          return;
        }
      }

      if (resolvedAuth) {
        console.log("");
        const workspaceSetup = await runWorkspaceSetup(
          {
            createWorkspace,
            getCurrentWorkspace,
            listWorkspaces,
            setDefaultWorkspace,
          },
          {
            cancel: clack.cancel,
            isCancel: clack.isCancel,
            select: selectPrompt,
            text: textPrompt,
          },
          {
            interactive,
            skipIfConfigured: !options.workspace,
          },
        );
        if (workspaceSetup.status === "cancelled") {
          return;
        }
        if (workspaceSetup.status === "reauth_requested") {
          console.log("");
          console.log(
            `  ${ICON.warn}  ${pc.yellow("workspace")}  ${pc.dim("API key authenticated but returned 0 workspaces — likely an identity mismatch.")}`,
          );
          console.log(`  ${pc.cyan(`${getCmd()} auth login`)}  ${pc.dim("→ re-link via OAuth, then rerun")}  ${pc.cyan(`${getCmd()} setup`)}`);
          await safeTrackWizardTelemetry(
            "workspace_bootstrapped",
            buildWorkspaceSetupTelemetryProperties(workspaceSetup, {
              command: "setup",
              interactive,
              preset: "standard",
            }),
          );
          return;
        }
        await safeTrackWizardTelemetry(
          "workspace_bootstrapped",
          buildWorkspaceSetupTelemetryProperties(workspaceSetup, {
            command: "setup",
            interactive,
            preset: "standard",
          }),
        );
        const resolvedWorkspace = workspaceSetup.workspace ?? await getCurrentWorkspace().catch(() => null);
        if (resolvedWorkspace && resolvedAuth) {
          // Reuse this result for the final doctor render below — we already
          // paid the round-trip and the workspace endpoint is the slowest
          // single check in the verification step.
          cachedWorkspaceCheck = {
            configured: true,
            ok: true,
            skipped: false,
            source: resolvedAuth.source,
            baseUrl: resolvedAuth.baseUrl,
            workspace: resolvedWorkspace,
            details: [
              `workspace id: ${resolvedWorkspace.id}`,
              ...(resolvedWorkspace.isDefault ? ["workspace is marked as default"] : []),
            ],
          };
        }
        if (resolvedWorkspace) {
          persistContinuityDefaults({ workspace: resolvedWorkspace });
        }
        if (workspaceSetup.workspace) {
          console.log(`  ${ICON.ok}  ${pc.green("workspace ")} ${pc.bold(workspaceSetup.workspace.name)}`);
        }

        // Daily Brief + Trust Loop onboarding. Idempotent locally and
        // server-side: repeat setup runs do not re-ask unless --daily-brief is
        // passed.
        const briefResult = await runDailyBriefOnboarding({
          force: Boolean(options.dailyBrief),
          interactive,
          skip: Boolean(options.skipDailyBrief),
          workspace: resolvedWorkspace,
          prompts: {
            cancel: clack.cancel,
            isCancel: clack.isCancel,
            text: textPrompt,
            select: selectPrompt,
            confirm: clack.confirm,
          },
        });
        if (briefResult.status === "cancelled") {
          console.log(`  ${ICON.skip}  ${pc.dim(briefResult.message)}`);
        } else if (briefResult.status === "skipped_user") {
          console.log(`  ${ICON.skip}  ${pc.dim(briefResult.message)}`);
        } else if (briefResult.status === "completed") {
          console.log(`  ${ICON.ok}  ${pc.green("daily brief")}  ${pc.dim(briefResult.message)}`);
        } else if (briefResult.status === "failed") {
          console.log(`  ${ICON.warn}  ${pc.yellow("daily brief")}  ${pc.dim(briefResult.message)}`);
        }

        // Intent capture — the orgx canonical entry point. One free-text line
        // gets classified into an agent + plan (ICP draft, first-wave, etc).
        // The agent sources people from the ICP downstream; we do not ask the
        // user to type contacts by hand.
        const intentResult = await maybeCaptureSetupIntent({
          interactive,
          workspace: resolvedWorkspace,
        });
        if (intentResult === "cancelled") {
          return;
        }

        const addOnResult = await maybeConfigureOptionalWorkspaceAddOns({
          ...(setupContext ? { context: setupContext } : {}),
          interactive,
          profile: setupProfile,
          telemetry: { command: "setup", preset: "standard" },
          workspace: resolvedWorkspace,
        });
        if (addOnResult === "cancelled") {
          return;
        }

        const pluginInstallResult = await maybeInstallOptionalCompanionPlugins({
          interactive,
          statuses: pluginStatuses,
          telemetry: { command: "setup", preset: "standard" },
        });
        if (pluginInstallResult === "cancelled") {
          return;
        }
      }

      // Compact final status — surfaces already shown above, just give the bottom line
      const doctor = await runDoctor(
        cachedWorkspaceCheck ? { cachedWorkspace: cachedWorkspaceCheck } : {},
      );
      const assessment = assessDoctorReport(doctor);
      const verification = summarizeSetupVerification(assessment, doctor);
      await safeTrackWizardTelemetry(
        "setup_verified",
        buildDoctorTelemetryProperties(doctor, assessment, verification, {
          command: "setup",
          preset: "standard",
        }),
      );
      const configuredCount = doctor.surfaces.filter((s) => s.configured).length;
      console.log("");
      if (assessment.issues.length === 0) {
        console.log(`  ${ICON.ok}  ${pc.green("You're all set.")}  ${pc.dim(`OrgX is active across ${configuredCount} editor${configuredCount !== 1 ? "s" : ""}`)}`);
        if (wasAlreadyPaired) {
          console.log(
            `  ${pc.dim("→")}  ${pc.dim("If you opened the browser onboarding, click")} ${pc.cyan("I've already paired")} ${pc.dim("to continue.")}`,
          );
        }
      } else {
        // Something needs attention — show full report
        printDoctorReport(doctor, assessment);
        if (verification.status === "error") {
          process.exitCode = 1;
        }
      }
    });

  program
    .command("deepseek")
    .description("Run the pinned DeepSeek Harness profile with refreshed OrgX authorization.")
    .argument("<prompt...>", "prompt to run in the DeepSeek Harness headless profile")
    .action(async (prompt: string[]) => {
      const result = await launchDeepseekWithOrgx(prompt);
      if (result.status !== 0) process.exitCode = result.status;
    });

  async function runPkceLogin(opts: {
    open?: boolean;
    telemetrySource?: string;
    timeout?: number;
  } = {}): Promise<boolean> {
    const spinner = createOrgxSpinner("Starting OrgX OAuth login");
    spinner.start();

    try {
      const pkceResult = await startPkceLogin({
        timeoutMs: (opts.timeout ?? 600) * 1_000,
        onAuthorizeUrl: (url) => {
          spinner.succeed("Authorization ready");
          console.log(`\n  ${pc.dim("Open this URL to authorize:")}`);
          console.log(`  ${pc.cyan(url)}\n`);
          if (opts.open !== false) {
            const openResult = openBrowser(url);
            if (!openResult.ok && openResult.error) {
              console.log(pc.yellow(`Browser open failed: ${openResult.error}`));
            }
          }
          spinner.start();
          spinner.text = "Waiting for browser authorization...";
        },
      });

      spinner.text = "Verifying token...";
      const baseUrl = normalizeOrgxBaseUrl(DEFAULT_ORGX_BASE_URL);
      const verification = await verifyOrgxAuth({
        apiKey: pkceResult.accessToken,
        keyPrefix: pkceResult.accessToken.slice(0, 12),
        baseUrl,
        source: "wizard-store",
      });

      if (!verification.ok) {
        spinner.fail("OAuth token verification failed");
        console.log(`       ${pc.red(verification.error ?? `HTTP ${verification.status ?? "error"}`)}`);
        console.log(`       ${pc.dim("If this persists, try: wizard auth set-key <oxk_...>")}`);
        return false;
      }

      const stored = await writeWizardAuth({
        apiKey: pkceResult.accessToken,
        baseUrl,
        verifiedAt: new Date().toISOString(),
        source: "pkce",
        ...(pkceResult.refreshToken !== undefined ? { refreshToken: pkceResult.refreshToken } : {}),
        oauthClientId: pkceResult.clientId,
      });

      await safeTrackWizardTelemetry("auth_completed", {
        auth_source: opts.telemetrySource ?? "pkce",
        has_base_url: false,
      });

      const openclawResults = detectSurface("openclaw").detected
        ? await addSurface("openclaw")
        : [];

      spinner.succeed("OrgX account paired");
      await syncContinuityAfterAuth();
      console.log(`  ${ICON.ok}  ${pc.green("paired    ")} ${pc.bold(stored.keyPrefix)}  ${pc.dim("browser sign-in")}`);

      if (openclawResults.length > 0) {
        console.log("");
        printMutationResults(openclawResults);
      }

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      spinner.fail("OAuth login failed");
      console.log(`       ${pc.red(message)}`);
      return false;
    }
  }

  function shortPairingId(pairingId: string): string {
    return pairingId.length > 13
      ? `${pairingId.slice(0, 8)}...${pairingId.slice(-4)}`
      : pairingId;
  }

  function formatPairingExpiry(expiresAt: string): string {
    const remainingMs = Date.parse(expiresAt) - Date.now();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "expired";
    const minutes = Math.ceil(remainingMs / 60_000);
    return minutes <= 1 ? "<1m" : `${minutes}m`;
  }

  async function runBrowserLogin(opts: {
    baseUrl?: string;
    deviceName?: string;
    open?: boolean;
    telemetrySource?: string;
    timeout?: number;
  } = {}): Promise<boolean> {
    const installationId = getOrCreateWizardInstallationId();
    const deviceName = opts.deviceName ?? hostname();
    const spinner = createOrgxSpinner("Starting OrgX browser pairing");
    spinner.start();

    try {
      const pairing = await startBrowserPairing({
        installationId,
        baseUrl: opts.baseUrl,
        deviceName,
        platform: process.platform,
      });
      spinner.succeed("Browser pairing ready");

      console.log(`\n  ${pc.dim("Open this URL to approve terminal pairing:")}`);
      console.log(`  ${pc.cyan(pairing.connectUrl)}\n`);
      console.log(
        `  ${pc.dim("session  ")} ${pc.bold(shortPairingId(pairing.pairingId))}  ${pc.dim("device")} ${pc.bold(deviceName)}  ${pc.dim("expires")} ${pc.bold(formatPairingExpiry(pairing.expiresAt))}\n`
      );
      if (opts.open !== false) {
        const openResult = openBrowser(pairing.connectUrl);
        if (!openResult.ok && openResult.error) {
          console.log(pc.yellow(`Browser open failed: ${openResult.error}`));
        }
      }

      spinner.start();
      spinner.text = "Waiting for browser approval...";
      const ready = await waitForBrowserPairing({
        baseUrl: opts.baseUrl,
        pairingId: pairing.pairingId,
        pollIntervalMs: pairing.pollIntervalMs,
        pollToken: pairing.pollToken,
        timeoutMs: (opts.timeout ?? 600) * 1000,
      });

      spinner.text = "Verifying paired account...";
      const result = await verifyAndPersistAuth(ready.key!, {
        ...(opts.baseUrl !== undefined ? { baseUrl: opts.baseUrl } : {}),
        ...(opts.telemetrySource ? { telemetrySource: opts.telemetrySource } : {}),
      });

      if (!("stored" in result)) {
        spinner.fail("Pairing completed but OrgX rejected the key");
        console.log(`       ${pc.red(result.verification.error ?? `HTTP ${result.verification.status ?? "error"}`)}`);
        console.log(`       ${pc.dim("The browser flow finished, but OrgX rejected the key. Try again or check your account at useorgx.com.")}`);
        return false;
      }

      let ackConfirmed = false;
      try {
        await acknowledgeBrowserPairing({
          baseUrl: opts.baseUrl,
          pairingId: pairing.pairingId,
          pollToken: pairing.pollToken,
        });
        ackConfirmed = true;
      } catch (error: unknown) {
        console.log(pc.dim(`Pairing ack failed: ${error instanceof Error ? error.message : String(error)}`));
      }

      spinner.succeed("OrgX account paired");
      await syncContinuityAfterAuth({
        executionMode: ready.executionMode,
        workspaceName: ready.workspaceName,
      });

      console.log(`  ${ICON.ok}  ${pc.green("paired    ")} ${pc.bold(result.stored.keyPrefix ?? result.verification.keyPrefix ?? "unknown")}  ${pc.dim("wizard auth store")}`);
      if (ready.workspaceName) {
        console.log(`  ${ICON.ok}  ${pc.green("workspace ")} ${pc.bold(ready.workspaceName)}`);
      }
      console.log(`  ${ackConfirmed ? ICON.ok : ICON.warn}  ${ackConfirmed ? pc.green("ack       ") : pc.yellow("ack       ")} ${pc.bold(ackConfirmed ? "terminal confirmed" : "browser delivery pending")}  ${pc.dim(shortPairingId(pairing.pairingId))}`);

      if (result.openclawResults.length > 0) {
        console.log("");
        printMutationResults(result.openclawResults);
      }

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      spinner.fail("Browser pairing failed");
      console.log(`       ${pc.red(message)}`);
      return false;
    }
  }

  const auth = program.command("auth").description("Pair this terminal with an OrgX account.");

  auth
    .command("status")
    .description("Show which OrgX account this terminal is paired with.")
    .action(async () => {
      const spinner = createOrgxSpinner("Checking OrgX auth");
      spinner.start();
      const status = await checkOrgxAuth();
      spinner.stop();
      console.log(pc.dim("  account"));
      printAuthStatus(status);
    });

  auth
    .command("login")
    .description("Open the browser pairing flow and store a scoped OrgX key for this terminal.")
    .option("--api-key <key>", "Bypass browser auth and verify this OrgX API key directly.")
    .option("--base-url <url>", "OrgX base URL (for OpenClaw pairing fallback).")
    .option("--device-name <name>", "Device name shown during OpenClaw pairing.")
    .option("--no-open", "Do not automatically open the browser auth URL.")
    .option("--pairing", "Force the terminal pairing fallback instead of browser sign-in.")
    .option("--timeout <seconds>", "How long to wait for browser auth before giving up.", parseTimeoutSeconds, 600)
    .action(async (options: { apiKey?: string; baseUrl?: string; deviceName?: string; open?: boolean; pairing?: boolean; timeout: number }) => {
      // Direct key path — CI / blocked browser fallback
      if (options.apiKey) {
        const spinner = createOrgxSpinner("Verifying OrgX API key");
        spinner.start();
        const result = await verifyAndPersistAuth(options.apiKey, {
          ...options,
          telemetrySource: "manual_api_key",
        });

        if (!("stored" in result)) {
          spinner.fail("OrgX API key verification failed");
          printAuthStatus(result.verification);
          return;
        }

        spinner.succeed("OrgX account paired");
        await syncContinuityAfterAuth();
        console.log(`  ${ICON.ok}  ${pc.green("paired    ")} ${pc.bold(result.stored.keyPrefix ?? result.verification.keyPrefix ?? "unknown")}  ${pc.dim("wizard auth store")}`);

        if (result.openclawResults.length > 0) {
          console.log("");
          printMutationResults(result.openclawResults);
        }
        return;
      }

      // Force OpenClaw pairing when explicitly requested
      if (options.pairing) {
        await runBrowserLogin({
          ...(options.baseUrl !== undefined ? { baseUrl: options.baseUrl } : {}),
          ...(options.deviceName !== undefined ? { deviceName: options.deviceName } : {}),
          ...(options.open !== undefined ? { open: options.open } : {}),
          telemetrySource: "browser_pairing",
          timeout: options.timeout,
        });
        return;
      }

      // Default: OAuth PKCE (works without OpenClaw), fall back to OpenClaw pairing
      const pkceOk = await runPkceLogin({
        ...(options.open !== undefined ? { open: options.open } : {}),
        telemetrySource: "pkce",
        timeout: options.timeout,
      });

      if (!pkceOk) {
        console.log(pc.dim("\n  Browser sign-in did not finish. Opening the terminal pairing fallback..."));
        await runBrowserLogin({
          ...(options.baseUrl !== undefined ? { baseUrl: options.baseUrl } : {}),
          ...(options.deviceName !== undefined ? { deviceName: options.deviceName } : {}),
          ...(options.open !== undefined ? { open: options.open } : {}),
          telemetrySource: "browser_pairing_fallback",
          timeout: options.timeout,
        });
      }
    });

  auth
    .command("set-key")
    .description("Verify and persist a per-user OrgX API key for the wizard.")
    .argument("<apiKey>", "per-user OrgX API key (oxk_...)")
    .option("--base-url <url>", "OrgX base URL")
    .action(async (apiKey: string, options: { baseUrl?: string }) => {
      const spinner = createOrgxSpinner("Verifying OrgX API key");
      spinner.start();
      const result = await verifyAndPersistAuth(apiKey, {
        ...options,
        telemetrySource: "manual_api_key",
      });

      if (!("stored" in result)) {
        spinner.fail("OrgX API key verification failed");
        printAuthStatus(result.verification);
        return;
      }

      spinner.succeed("OrgX account paired");
      await syncContinuityAfterAuth();
      console.log(`  ${ICON.ok}  ${pc.green("paired    ")} ${pc.bold(result.stored.keyPrefix ?? result.verification.keyPrefix ?? "unknown")}  ${pc.dim("wizard auth store")}`);

      if (result.openclawResults.length > 0) {
        console.log("");
        printMutationResults(result.openclawResults);
      }
    });

  auth
    .command("clear")
    .description("Remove the wizard-local saved OrgX API key.")
    .action(async () => {
      const removed = await clearWizardAuth();
      if (removed) {
        console.log(pc.green("Removed the wizard-local OrgX API key."));
      } else {
        console.log(pc.yellow("No wizard-local OrgX API key was stored."));
      }

      const resolved = await resolveOrgxAuth();
      if (resolved) {
        console.log(
          `Effective OrgX auth still resolves from ${formatAuthSource(resolved.source)} (${resolved.keyPrefix}).`,
        );
      }
    });

  program
    .command("uninstall")
    .description("Remove OrgX-managed MCP tool configs, companion plugins, auth, and wizard state from this machine.")
    .option("--keep-auth", "Keep the wizard-local saved OrgX API key.")
    .option("--keep-state", "Keep wizard-local setup state.")
    .option("--skip-plugins", "Skip companion plugin and legacy Cursor rules removal.")
    .option("--skip-surfaces", "Skip MCP tool config removal.")
    .action(async (options: {
      keepAuth?: boolean;
      keepState?: boolean;
      skipPlugins?: boolean;
      skipSurfaces?: boolean;
    }) => {
      await safeTrackWizardTelemetry("wizard_uninstalled", {
        keep_auth: Boolean(options.keepAuth),
        keep_state: Boolean(options.keepState),
        skip_plugins: Boolean(options.skipPlugins),
        skip_surfaces: Boolean(options.skipSurfaces),
      });

      console.log(pc.bold("Removing OrgX wizard-managed configuration"));

      if (options.skipSurfaces) {
        console.log(`  ${ICON.skip}  ${pc.bold("surfaces".padEnd(10))} ${pc.dim("skipped")}`);
      } else {
        console.log("");
        console.log(pc.dim("  surfaces"));
        const surfaceResults = removeSurface(["all"]);
        printMutationResults(surfaceResults);
      }

      if (options.skipPlugins) {
        console.log(`  ${ICON.skip}  ${pc.bold("plugins".padEnd(12))} ${pc.dim("skipped")}`);
      } else {
        console.log("");
        console.log(pc.dim("  plugins"));
        const spinner = createOrgxSpinner("Removing OrgX companion plugins and legacy Cursor rules");
        spinner.start();
        const pluginReport = await uninstallOrgxPlugins({ targets: ["all"] });
        spinner.succeed("OrgX companion plugins and legacy Cursor rules processed");
        printPluginMutationReport(pluginReport);
      }

      console.log("");
      if (options.keepAuth) {
        console.log(`  ${ICON.skip}  ${pc.bold("auth".padEnd(12))} ${pc.dim("kept")}`);
      } else {
        const removedAuth = await clearWizardAuth();
        const state = removedAuth ? pc.green("updated  ") : pc.dim("unchanged");
        const message = removedAuth
          ? "Removed the wizard-local OrgX API key."
          : "No wizard-local OrgX API key was stored.";
        console.log(`  ${removedAuth ? ICON.ok : ICON.skip}  ${pc.bold("auth".padEnd(12))} ${state}  ${pc.dim(message)}`);
      }

      if (options.keepState) {
        console.log(`  ${ICON.skip}  ${pc.bold("state".padEnd(12))} ${pc.dim("kept")}`);
      } else {
        const removedState = clearWizardState();
        const state = removedState ? pc.green("updated  ") : pc.dim("unchanged");
        const message = removedState
          ? "Removed wizard-local setup state."
          : "No wizard-local setup state was stored.";
        console.log(`  ${removedState ? ICON.ok : ICON.skip}  ${pc.bold("state".padEnd(12))} ${state}  ${pc.dim(message)}`);
      }
    });

  const surface = program.command("surface").description("Manage supported OrgX surfaces.");

  surface
    .command("list")
    .description("Show supported surfaces and their current status.")
    .action(() => {
      printSurfaceTable(listSurfaceStatuses());
    });

  surface
    .command("add")
    .description("Patch one surface or all automated surfaces.")
    .argument("<names...>", "surface names or 'all'")
    .action(async (names: string[]) => {
      const results = await addSurface(names as Parameters<typeof addSurface>[0]);
      printMutationResults(results);
    });

  surface
    .command("remove")
    .description("Remove OrgX-managed config from one surface or all automated surfaces.")
    .argument("<names...>", "surface names or 'all'")
    .action((names: string[]) => {
      const results = removeSurface(names as Parameters<typeof removeSurface>[0]);
      printMutationResults(results);
    });

  const mcp = program
    .command("mcp")
    .description("Add or remove OrgX MCP config entries in Claude, Cursor, Codex, VS Code, Windsurf, and Zed.");

  mcp
    .command("add")
    .description("Add OrgX MCP entries to one tool config or all supported MCP tool configs.")
    .argument("[surfaces...]", "claude, cursor, codex, vscode, windsurf, zed, or all", ["all"])
    .action(async (names: string[]) => {
      const results = await addMcpSurface(names as Parameters<typeof addMcpSurface>[0]);
      printMutationResults(results);
    });

  mcp
    .command("remove")
    .description("Remove OrgX MCP entries from one tool config or all supported MCP tool configs.")
    .argument("[surfaces...]", "claude, cursor, codex, vscode, windsurf, zed, or all", ["all"])
    .action((names: string[]) => {
      const results = removeMcpSurface(names as Parameters<typeof removeMcpSurface>[0]);
      printMutationResults(results);
    });

  const plugins = program
    .command("plugins")
    .description("Install or remove OrgX companion plugins in Cursor, Claude Code, Codex, OpenCode, and OpenClaw.");

  plugins
    .command("list")
    .description("Show companion plugin availability and install status in your tools.")
    .action(async () => {
      const spinner = createOrgxSpinner("Checking OrgX companion plugin status");
      spinner.start();
      const statuses = await listOrgxPluginStatuses();
      spinner.succeed("OrgX companion plugin status checked");
      printPluginStatusReport(statuses);
    });

  plugins
    .command("add")
    .description("Install OrgX companion plugins into Cursor, Claude Code, Codex, OpenCode, and OpenClaw.")
    .argument("[targets...]", "cursor, claude, codex, opencode, openclaw, or all", ["all"])
    .action(async (targets: string[]) => {
      const spinner = createOrgxSpinner("Installing OrgX companion plugins");
      spinner.start();
      const report = await installOrgxPlugins({ targets });
      spinner.succeed("OrgX companion plugins processed");
      printPluginMutationReport(report);
      printPluginSkillOwnershipNote(report.results.map((result) => result.target));
      await safeTrackWizardTelemetry("plugins_installed", {
        changed_count: countPluginReportChanges(report),
        command: "plugins:add",
        requested_target_count: targets.length,
        target_count: report.results.length,
      });
    });

  plugins
    .command("remove")
    .description("Uninstall managed OrgX companion plugins and legacy managed Cursor rules from Cursor, Claude Code, Codex, OpenCode, and OpenClaw.")
    .argument("[targets...]", "cursor, claude, codex, opencode, openclaw, or all", ["all"])
    .action(async (targets: string[]) => {
      const spinner = createOrgxSpinner("Removing OrgX companion plugins and legacy Cursor rules");
      spinner.start();
      const report = await uninstallOrgxPlugins({ targets });
      spinner.succeed("OrgX companion plugins and legacy Cursor rules processed");
      printPluginMutationReport(report);
      await safeTrackWizardTelemetry("plugins_removed", {
        changed_count: countPluginReportChanges(report),
        command: "plugins:remove",
        requested_target_count: targets.length,
        target_count: report.results.length,
      });
    });

  const workspace = program
    .command("workspace")
    .description("Inspect and create OrgX workspaces.");

  workspace
    .command("current")
    .description("Show the current OrgX workspace.")
    .action(async () => {
      const spinner = createOrgxSpinner("Loading current OrgX workspace");
      spinner.start();
      const current = await getCurrentWorkspace();
      spinner.stop();

      if (!current) {
        console.log(pc.yellow("No OrgX workspaces found for the current user."));
        return;
      }

      printWorkspace(current);
    });

  workspace
    .command("list")
    .description("List OrgX workspaces accessible with the current API key.")
    .action(async () => {
      const spinner = createOrgxSpinner("Loading OrgX workspaces");
      spinner.start();
      const workspaces = await listWorkspaces();
      spinner.stop();
      printWorkspaceList(workspaces);
    });

  workspace
    .command("create")
    .description("Create a new OrgX workspace.")
    .argument("<name>", "workspace name")
    .option("--description <text>", "workspace description")
    .action(async (name: string, options: { description?: string }) => {
      const spinner = createOrgxSpinner("Creating OrgX workspace");
      spinner.start();
      const created = await createWorkspace(
        {
          name,
          ...(options.description ? { description: options.description } : {}),
        },
      );
      spinner.succeed("OrgX workspace created");
      if (created.isDefault) {
        persistContinuityDefaults({ workspace: created });
      }
      printWorkspace(created);
      if (!created.isDefault) {
        console.log("");
        console.log(pc.dim(`Run \`wizard workspace set-default ${created.id}\` to promote it.`));
      }
    });

  workspace
    .command("set-default")
    .description("Set the default OrgX workspace.")
    .argument("<id>", "workspace id")
    .action(async (id: string) => {
      const spinner = createOrgxSpinner("Updating default OrgX workspace");
      spinner.start();
      const result = await setDefaultWorkspace({ id });
      spinner.succeed(
        result.changed ? "Default OrgX workspace updated" : "OrgX workspace already set as default",
      );
      persistContinuityDefaults({ workspace: result.workspace });
      printWorkspace(result.workspace);
    });

  program
    .command("intent")
    .description(
      "Capture an intent, preview the classified agent + plan, and approve or hand off to the browser.",
    )
    .argument("[text...]", "intent text (quotes optional)")
    .option("--workspace <id>", "workspace id override; defaults to the current workspace")
    .option("--json", "emit a JSON summary instead of the box-drawn preview")
    .action(async (textParts: string[], options: { workspace?: string; json?: boolean }) => {
      const initialText = textParts.join(" ").trim();
      const workspaceId = options.workspace?.trim() ?? "";
      await runIntentCommand({
        ...(initialText ? { initialText } : {}),
        ...(workspaceId ? { workspaceId } : {}),
        jsonOutput: Boolean(options.json),
      });
    });

  program
    .command("audit")
    .description("Run the AI-native Founder Loop self-audit from pasted or file-based AI-session context.")
    .option("--input <path>", "AI-session transcript or summary file; stdin is used when piped")
    .option("--from <sources>", "auto-import recent local AI sessions: claude, codex, opencode, goose, cursor, github, slack, or all")
    .option("--session-limit <count>", "max recent sessions to import per selected source", "3")
    .option("--session-days <days>", "lookback window for local AI-session imports", "30")
    .option("--codex-sessions-dir <path>", "override Codex sessions directory for audit import")
    .option("--claude-projects-dir <path>", "override Claude projects directory for audit import")
    .option("--source-label <label>", "label for the imported AI-session source")
    .option("--workspace-id <id>", "workspace id override; defaults to the current OrgX workspace when authenticated")
    .option("--workspace-name <name>", "workspace name override for local-only audits")
    .option("--output-dir <path>", "directory for generated audit JSON and Markdown", ".orgx/audits")
    .option("--attach-to-initiative <id>", "attach the generated audit artifact to an existing OrgX initiative")
    .option("--create-initiative", "create an OrgX initiative from the generated audit plan")
    .option("--write-follow-up", "create one approval-gated OrgX follow-up task from the audit recommendation")
    .option("--workstream-id <id>", "optional workstream id for the generated follow-up task")
    .option("--milestone-id <id>", "optional milestone id for the generated follow-up task")
    .option("--dry-run", "exercise OrgX write paths without sending writes")
    .option("--yes", "approve OrgX write flags in non-interactive mode")
    .option("--json", "emit a JSON command summary")
    .action(async (options: AuditCommandOptions) => {
      await safeTrackWizardTelemetry("audit_started", {
        attach_to_initiative: Boolean(options.attachToInitiative),
        command: "audit",
        create_initiative: Boolean(options.createInitiative),
        dry_run: Boolean(options.dryRun),
        write_follow_up: Boolean(options.writeFollowUp),
      });
      await runAuditCommand(options);
    });

  program
    .command("map")
    .alias("discovery")
    .description("Map observed company workflows into evidence-gated OperatingProcess candidates.")
    .argument("[query...]", "workflow, handoff, or system-of-record question for discovery/deep search")
    .option("--deep-search", "query cited external research in addition to connected repository signals")
    .option("--source <kinds>", "comma-separated source kinds to report and constrain (for example github,notion,slack)")
    .option("--workspace-id <id>", "workspace id override; defaults to the current OrgX workspace")
    .option("--candidate <id-or-number>", "propose one returned ProcessCard by id or 1-based display number")
    .option("--idempotency-key <key>", "stable retry key; defaults to a hash of workspace, mode, query, and sources")
    .option("--yes", "approve the explicit proposal in non-interactive mode")
    .option("--json", "emit machine-readable discovery/proposal output")
    .action(async (queryParts: string[], options: OperatingMapCommandOptions) => {
      await safeTrackWizardTelemetry("operating_map_started", {
        command: "map",
        deep_search: Boolean(options.deepSearch),
        has_candidate: Boolean(options.candidate),
        json: Boolean(options.json),
      });
      await runOperatingMapCommand(queryParts, options);
    });

  const workGraph = program
    .command("work-graph")
    .description("Run AQ from real AI-work receipts and surface the first repair that raises execution capacity.");

  workGraph
    .command("extraction-schema")
    .description("Print the packaged AI-client audit skill used to search sessions, messages, tools, domains, and logs.")
    .option("--output <path>", "write the schema prompt to a file")
    .option("--json", "emit the protocol as JSON instead of Markdown")
    .action(async (options: WorkGraphExtractionSchemaCommandOptions) => {
      await safeTrackWizardTelemetry("work_graph_extraction_schema_started", {
        command: "work-graph extraction-schema",
        json: Boolean(options.json),
      });
      runWorkGraphExtractionSchemaCommand(options);
    });

  workGraph
    .command("runtime-event")
    .description("Write a redacted Codex/Claude runtime packet into the Work Graph collector directory.")
    .requiredOption("--source <source>", "agent source writing the packet: codex or claude")
    .option("--summary <text>", "public-safe summary of the decision, artifact, blocker, outcome, or tool event")
    .option("--message <text>", "alias for --summary")
    .option("--event-kind <kind>", "event kind hint: decision, artifact, blocker, outcome, tool_call_error", "artifact")
    .option("--role <role>", "source role: user, assistant, tool, or meta", "assistant")
    .option("--tool-name <name>", "tool name when the packet represents a tool call")
    .option("--cwd <path>", "workspace root that owns the collector directory")
    .option("--output-dir <path>", "collector root relative to cwd", ".orgx/work-graph/runtime-events")
    .option("--json", "emit a JSON summary")
    .action((options: WorkGraphRuntimeEventCommandOptions) => {
      runWorkGraphRuntimeEventCommand(options);
    });

  workGraph
    .command("preview")
    .description("Preview AQ, evidence paths, and the first repair without writing to OrgX.")
    .option("--input <path>", "source transcript or summary file; stdin is used when piped")
    .option("--extraction <path>", "structured AI-client extraction JSON; repeat or comma-separate for multiple clients", collectPathOption, [])
    .option("--from <sources>", "auto-import recent local AI sessions: claude, codex, opencode, goose, cursor, github, slack, or all")
    .option("--session-limit <count>", "max recent sessions to import per selected source", "15")
    .option("--session-days <days>", "lookback window for local AI-session imports", "60")
    .option("--codex-sessions-dir <path>", "override Codex sessions directory for import")
    .option("--claude-projects-dir <path>", "override Claude projects directory for import")
    .option("--source-label <label>", "label for the imported manual source")
    .option("--workspace-id <id>", "workspace id override; defaults to the current OrgX workspace when authenticated")
    .option("--workspace-name <name>", "workspace name override for local-only previews")
    .option("--output-dir <path>", "directory for generated Work Graph JSON and Markdown", ".orgx/work-graph")
    .option("--json", "emit a JSON command summary")
    .action(async (options: WorkGraphCommandOptions) => {
      await safeTrackWizardTelemetry("work_graph_preview_started", {
        command: "work-graph preview",
        from: options.from ?? "manual",
      });
      await runWorkGraphCommand(options);
    });

  workGraph
    .command("profile")
    .description("Build an AQ profile from real receipts, publish it, and return the first executable repair.")
    .option("--input <path>", "source transcript or summary file; stdin is used when piped")
    .option("--extraction <path>", "structured AI-client extraction JSON; repeat or comma-separate for multiple clients", collectPathOption, [])
    .option("--from <sources>", "auto-import recent local AI sessions: claude, codex, opencode, goose, cursor, github, slack, or all")
    .option("--session-limit <count>", "max recent sessions to import per selected source", "25")
    .option("--session-days <days>", "lookback window for local AI-session imports", "60")
    .option("--codex-sessions-dir <path>", "override Codex sessions directory for import")
    .option("--claude-projects-dir <path>", "override Claude projects directory for import")
    .option("--source-label <label>", "label for the imported manual source")
    .option("--workspace-id <id>", "workspace id override; defaults to the current OrgX workspace when authenticated")
    .option("--workspace-name <name>", "workspace name override for local-only profiles")
    .option("--output-dir <path>", "directory for generated Work Graph JSON and Markdown", ".orgx/work-graph")
    .option("--publish", "publish the generated OrgX Profile to OrgX and return a shareable URL")
    .option("--public-share", "create a public redacted /work-graph/<token> share when publishing")
    .option("--attach-artifact", "attach the report as an OrgX artifact when an initiative/entity is provided")
    .option("--attach-to-initiative <id>", "attach the report to an existing OrgX initiative")
    .option("--entity-type <type>", "entity type for artifact attachment: project, initiative, milestone, task, decision")
    .option("--entity-id <id>", "entity id for artifact attachment")
    .option("--artifact-url <url>", "override the artifact URL stored in OrgX")
    .option("--yes", "approve publish/write prompts in non-interactive mode")
    .option("--json", "emit a JSON command summary")
    .action(async (options: WorkGraphCommandOptions) => {
      await safeTrackWizardTelemetry("work_graph_profile_started", {
        command: "work-graph profile",
        from: options.from ?? "manual",
      });
      await runWorkGraphCommand(options);
    });

  const sessions = program
    .command("sessions")
    .description("Inspect and reconcile local AI sessions into OrgX-ready Work Graph reports.");

  sessions
    .command("reconcile")
    .description("Backfill recent Claude Code, Codex, OpenCode, goose, and Cursor sessions into a redacted OrgX Profile report.")
    .option("--extraction <path>", "structured AI-client extraction JSON; repeat or comma-separate for multiple clients", collectPathOption, [])
    .option("--from <sources>", "auto-import recent local AI sessions: claude, codex, opencode, goose, cursor, github, slack, or all", "all")
    .option("--session-limit <count>", "max recent sessions to import per selected source", "25")
    .option("--session-days <days>", "lookback window for local AI-session imports", "30")
    .option("--codex-sessions-dir <path>", "override Codex sessions directory for import")
    .option("--claude-projects-dir <path>", "override Claude projects directory for import")
    .option("--workspace-id <id>", "workspace id override; defaults to the current OrgX workspace when authenticated")
    .option("--workspace-name <name>", "workspace name override for local-only reconciliation")
    .option("--output-dir <path>", "directory for generated Work Graph JSON and Markdown", ".orgx/work-graph")
    .option("--publish", "publish the generated OrgX Profile to OrgX and return a shareable URL")
    .option("--public-share", "create a public redacted /work-graph/<token> share when publishing")
    .option("--attach-artifact", "attach the report as an OrgX artifact when an initiative/entity is provided")
    .option("--attach-to-initiative <id>", "attach the report to an existing OrgX initiative")
    .option("--entity-type <type>", "entity type for artifact attachment: project, initiative, milestone, task, decision")
    .option("--entity-id <id>", "entity id for artifact attachment")
    .option("--artifact-url <url>", "override the artifact URL stored in OrgX")
    .option("--yes", "approve publish/write prompts in non-interactive mode")
    .option("--json", "emit a JSON command summary")
    .action(async (options: WorkGraphCommandOptions) => {
      await safeTrackWizardTelemetry("sessions_reconcile_started", {
        command: "sessions reconcile",
        from: options.from ?? "all",
      });
      await runWorkGraphCommand(options, { from: "all" });
    });

  const sessionContext = sessions
    .command("context")
    .description("Manage the short-lived, exact-directory context used for automatic execution receipts.");

  sessionContext
    .command("set")
    .description("Validate and activate producer-asserted work context for the next local agent session.")
    .requiredOption("--file <path>", "orgx-session-work-context/v1 JSON file, or - for stdin")
    .option("--cwd <path>", "exact working directory allowed to consume the context", process.cwd())
    .option("--source-client <client>", "native client binding, paired with --session-id")
    .option("--session-id <id>", "native session binding, paired with --source-client")
    .option("--context-sha256 <digest>", "required canonical context fingerprint")
    .option("--ttl-minutes <minutes>", "activation lifetime, from 1 to 1440 minutes", "480")
    .option("--json", "emit a JSON command summary")
    .action(async (options: SessionContextCommandOptions) => {
      const source = options.file?.trim();
      if (!source) throw new Error("Missing --file <path> for session work context.");
      const raw = source === "-" ? readFileSync(0, "utf8") : readFileSync(resolve(source), "utf8");
      let context: unknown;
      try {
        context = JSON.parse(raw) as unknown;
      } catch {
        throw new Error("Session work context must be valid JSON.");
      }
      if (Boolean(options.sourceClient?.trim()) !== Boolean(options.sessionId?.trim())) {
        throw new Error("--source-client and --session-id must be supplied together.");
      }
      const expectedContextSha256 = options.contextSha256?.trim().toLowerCase();
      if (expectedContextSha256 && !/^[0-9a-f]{64}$/.test(expectedContextSha256)) {
        throw new Error("--context-sha256 must be a lowercase 64-character SHA-256 digest.");
      }
      const activation = createSessionWorkContextActivation({
        context,
        cwd: options.cwd ?? process.cwd(),
        ttlMinutes: parsePositiveInt(options.ttlMinutes, 480),
        ...(options.sourceClient?.trim()
          ? { sourceClient: options.sourceClient.trim() }
          : {}),
        ...(options.sessionId?.trim() ? { sessionId: options.sessionId.trim() } : {}),
        ...(expectedContextSha256 ? { expectedContextSha256 } : {}),
      });
      writeSessionWorkContextActivation(activation);
      const report = inspectSessionWorkContextActivation({
        cwd: options.cwd ?? process.cwd(),
        ...(options.sourceClient?.trim()
          ? { sourceClient: options.sourceClient.trim() }
          : {}),
        ...(options.sessionId?.trim() ? { sessionId: options.sessionId.trim() } : {}),
      });
      await safeTrackWizardTelemetry("session_work_context_activated", {
        command: "sessions context set",
        ttl_minutes: options.ttlMinutes ?? "480",
      });
      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
        return;
      }
      printSessionWorkContextInspection(report);
      console.log(`  ${ICON.skip}  ${pc.dim("Start the matching client from this exact directory before the activation expires. A cwd-only activation is claimed once by the first supported native session.")}`);
    });

  sessionContext
    .command("show")
    .description("Inspect whether the current directory has receipt-ready work context.")
    .option("--cwd <path>", "working directory to inspect", process.cwd())
    .option("--source-client <client>", "native client binding, paired with --session-id")
    .option("--session-id <id>", "native session binding, paired with --source-client")
    .option("--json", "emit a JSON command summary")
    .action((options: SessionContextCommandOptions) => {
      if (Boolean(options.sourceClient?.trim()) !== Boolean(options.sessionId?.trim())) {
        throw new Error("--source-client and --session-id must be supplied together.");
      }
      const report = inspectSessionWorkContextActivation({
        cwd: options.cwd ?? process.cwd(),
        ...(options.sourceClient?.trim()
          ? { sourceClient: options.sourceClient.trim() }
          : {}),
        ...(options.sessionId?.trim() ? { sessionId: options.sessionId.trim() } : {}),
      });
      if (!report.ready) process.exitCode = 1;
      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
        return;
      }
      printSessionWorkContextInspection(report);
    });

  sessionContext
    .command("clear")
    .description("Clear the active local session work context.")
    .option("--cwd <path>", "working directory whose context should be cleared", process.cwd())
    .option("--source-client <client>", "native client binding, paired with --session-id")
    .option("--session-id <id>", "native session binding, paired with --source-client")
    .option("--json", "emit a JSON command summary")
    .action((options: SessionContextCommandOptions) => {
      const cwd = options.cwd ?? process.cwd();
      if (Boolean(options.sourceClient?.trim()) !== Boolean(options.sessionId?.trim())) {
        throw new Error("--source-client and --session-id must be supplied together.");
      }
      const identity = options.sourceClient?.trim() && options.sessionId?.trim()
        ? {
            sourceClient: options.sourceClient.trim(),
            sessionId: options.sessionId.trim(),
          }
        : undefined;
      const path = sessionWorkContextActivationPath(cwd, undefined, identity);
      const cleared = clearSessionWorkContextActivation(cwd, path);
      const report = inspectSessionWorkContextActivation({
        cwd,
        path,
        ...(identity ?? {}),
      });
      if (options.json) {
        console.log(JSON.stringify({ cleared, ...report }, null, 2));
        return;
      }
      console.log(`  ${cleared ? ICON.ok : ICON.skip}  ${pc.bold("context     ")} ${pc.dim(cleared ? "cleared" : "already clear")}`);
    });

  const hooks = program
    .command("hooks")
    .description("Inspect or install passive OrgX runtime hooks for local agent clients.");

  hooks
    .command("doctor")
    .description("Show four-client continuity, runtime hook, queue, and delivery status.")
    .option("--cwd <path>", "working directory whose receipt context should be inspected", process.cwd())
    .option("--json", "emit a JSON command summary")
    .action(async (options: HooksCommandOptions) => {
      const report = inspectRuntimeHooks({ cwd: options.cwd ?? process.cwd() });
      await safeTrackWizardTelemetry("hooks_doctor_ran", {
        command: "hooks doctor",
        codex_installed: report.installed.codex,
        claude_code_installed: report.installed.claudeCode,
        hook_script_installed: report.installed.hookScript,
      });
      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
        return;
      }
      printRuntimeHookInspection(report);
    });

  hooks
    .command("install")
    .description("Install passive OrgX runtime hooks for Codex and/or Claude Code.")
    .option("--targets <targets>", "comma-separated targets: codex, claude-code, or all", "all")
    .option(
      "--work-capture <mode>",
      "bounded captures redacted request excerpts; metadata-only never retains prompt content",
      "bounded",
    )
    .option("--json", "emit a JSON command summary")
    .action(async (options: HooksCommandOptions) => {
      const targets = parseRuntimeHookTargets(options.targets);
      const workEpisodeCapture = parseRuntimeHookWorkEpisodeCapture(
        options.workCapture,
      );
      const result = installRuntimeHooks(targets, { workEpisodeCapture });
      await safeTrackWizardTelemetry("hooks_install_ran", {
        command: "hooks install",
        targets: targets.join(","),
        codex_changed: result.changed.codex,
        claude_code_changed: result.changed.claudeCode,
      });
      if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }
      printRuntimeHookInspection(result);
      if (result.backups.length > 0) {
        console.log("");
        console.log(pc.dim("  backups"));
        for (const backup of result.backups) {
          console.log(`  ${ICON.skip}  ${pc.dim(backup)}`);
        }
      }
    });

  hooks
    .command("flush")
    .description("Deliver queued session summaries and retain every unacknowledged capture.")
    .option("--queue <path>", "session-summary capture queue path")
    .option("--limit <count>", "maximum queued captures to attempt", "100")
    .option("--background", "run quietly as a hook-triggered delivery worker")
    .option("--json", "emit a JSON command summary")
    .action(async (options: HooksCommandOptions) => {
      await runSessionSummaryFlushCommand(options);
    });

  hooks
    .command("replay")
    .description("Replay passive hook outbox events into a claimed Work Graph profile.")
    .requiredOption("--fingerprint <fingerprint>", "target Work Graph fingerprint, for example wgf_0123...")
    .option("--outbox <path>", "runtime hook JSONL outbox path")
    .option("--limit <count>", "maximum recent hook events to replay", "200")
    .option("--yes", "approve publishing hook evidence without prompting")
    .option("--json", "emit a JSON command summary")
    .action(async (options: HooksCommandOptions) => {
      await runHookReplayCommand(options);
    });

  hooks
    .command("backfill")
    .description("Distill the historical hook spool into lean session summaries (preview by default).")
    .option("--spool <path>", "runtime hook JSONL spool path")
    .option("--post", "upload the distilled summaries to OrgX (requires --yes)")
    .option("--truncate", "archive and empty the spool after a fully successful --post (requires --yes)")
    .option("--yes", "confirm uploading or truncating your local work history")
    .option("--json", "emit a JSON command summary")
    .action(async (options: HooksBackfillCommandOptions) => {
      await runHookBackfillCommand(options);
    });

  program
    .command("doctor")
    .description("Verify OrgX health, or diagnose a credential-free workload shape.")
    .option("--workload <path>", "diagnose a sanitized workload-shape JSON file; use - for stdin")
    .option("--base-url <url>", "explicit workload endpoint override for testing or self-hosting")
    .option("--json", "emit the selected doctor result as JSON")
    .action(async (options: DoctorCommandOptions) => {
      if (options.baseUrl && !options.workload) {
        const message = "--base-url is only valid together with --workload.";
        if (options.json) {
          console.log(JSON.stringify({ error: { code: "invalid_options", message } }, null, 2));
        } else {
          console.error(`  ${ICON.err}  ${pc.red(message)}`);
        }
        process.exitCode = 1;
        return;
      }
      let workloadInput: ReturnType<typeof readWorkloadDiagnosisInput> | null = null;
      if (options.workload) {
        try {
          workloadInput = readWorkloadDiagnosisInput(options.workload);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (options.json) {
            console.log(JSON.stringify({ error: { code: "invalid_workload_input", message } }, null, 2));
          } else {
            console.error(`  ${ICON.err}  ${pc.red(message)}`);
          }
          process.exitCode = 1;
          return;
        }
      }

      if (workloadInput) {
        const spinner = options.json
          ? null
          : createOrgxSpinner("Checking workload boundaries");
        spinner?.start();
        const workloadResult = await requestWorkloadDiagnosis(workloadInput, {
          ...(options.baseUrl ? { baseUrl: options.baseUrl } : {}),
        })
          .then((diagnosis) => ({ diagnosis, error: null }))
          .catch((error: unknown) => ({
            diagnosis: null,
            error: error instanceof Error ? error.message : String(error),
          }));
        spinner?.stop();

        if (options.json) {
          console.log(JSON.stringify({
            workload: workloadResult.diagnosis,
            workload_error: workloadResult.error,
          }, null, 2));
        } else if (workloadResult.diagnosis) {
          printWorkloadDiagnosis(workloadResult.diagnosis);
        } else {
          console.error(`  ${ICON.err}  ${pc.red("Workload diagnosis failed.")}  ${pc.dim(workloadResult.error ?? "Unknown error.")}`);
        }

        if (workloadResult.error) process.exitCode = 1;
        return;
      }

      const spinner = options.json
        ? null
        : createOrgxSpinner("Running OrgX health check");
      spinner?.start();
      const report = await runDoctor();
      spinner?.stop();
      const assessment = assessDoctorReport(report);
      const verification = summarizeSetupVerification(assessment, report);
      await safeTrackWizardTelemetry(
        "doctor_ran",
        buildDoctorTelemetryProperties(report, assessment, verification, {
          command: "doctor",
        }),
      );

      if (options.json) {
        console.log(JSON.stringify({
          health: { report, assessment, verification },
        }, null, 2));
      } else {
        printDoctorReport(report, assessment);
      }

      if (verification.status === "error") {
        process.exitCode = 1;
      }
    });

  const skills = program
    .command("skills")
    .description("Install OrgX skills and rules into supported local tools.");

  skills
    .command("add")
    .description("Write standalone OrgX Cursor rules and Claude skills, skipping tool surfaces already owned by companion plugins.")
    .argument("[packs...]", "skill pack names or 'all'", ["all"])
    .option("--force", "Overwrite generated skill files even when manual edits are detected.")
    .option("--ref <gitref>", "Install skills from a specific useorgx/skills branch, tag, or commit instead of main.")
    .action(async (packs: string[], options: { force?: boolean; ref?: string }) => {
      const pluginTargets = (await listOrgxPluginStatuses())
        .filter((status) => status.installed)
        .map((status) => status.target);
      const spinner = createOrgxSpinner("Installing OrgX skills/rules into tools");
      spinner.start();
      const report = await installOrgxSkills({
        force: options.force === true,
        pluginTargets,
        ...(options.ref ? { ref: options.ref } : {}),
        skillNames: packs,
      });
      spinner.succeed("OrgX skills/rules installed into tools");
      printSkillInstallReport(report);
      await safeTrackWizardTelemetry("skills_installed", {
        changed_count: countSkillReportChanges(report),
        command: "skills:add",
        pack_count: report.packs.length,
        plugin_managed_target_count: pluginTargets.length,
        requested_pack_count: packs.length,
        skill_extension_count: report.extensions.length,
        write_count: report.writes.length,
      });
    });

  skills
    .command("sync")
    .description("Recompose installed OrgX skills/rules from core skills plus local extensions.")
    .argument("[packs...]", "skill pack names or 'all'", ["all"])
    .option("--force", "Overwrite generated skill files even when manual edits are detected.")
    .option("--ref <gitref>", "Sync skills from a specific useorgx/skills branch, tag, or commit instead of main.")
    .action(async (packs: string[], options: { force?: boolean; ref?: string }) => {
      const pluginTargets = (await listOrgxPluginStatuses())
        .filter((status) => status.installed)
        .map((status) => status.target);
      const spinner = createOrgxSpinner("Syncing OrgX skills/rules and extensions into tools");
      spinner.start();
      const report = await installOrgxSkills({
        force: options.force === true,
        pluginTargets,
        ...(options.ref ? { ref: options.ref } : {}),
        skillNames: packs,
      });
      spinner.succeed("OrgX skills/rules synced into tools");
      printSkillInstallReport(report);
      await safeTrackWizardTelemetry("skills_synced", {
        changed_count: countSkillReportChanges(report),
        command: "skills:sync",
        pack_count: report.packs.length,
        plugin_managed_target_count: pluginTargets.length,
        requested_pack_count: packs.length,
        skill_extension_count: report.extensions.length,
        write_count: report.writes.length,
      });
    });

  skills
    .command("status")
    .description("Show installed skill/rule tracking and local skill extensions.")
    .action(async () => {
      const report = getSkillStatus();
      console.log(pc.dim("  extensions"));
      printSkillExtensions(report.extensions);
      console.log("");
      console.log(pc.dim("  generated files"));
      if (report.trackedFiles.length === 0) {
        console.log(`  ${ICON.skip}  ${pc.dim("no generated skill files tracked yet")}`);
      } else {
        for (const file of report.trackedFiles) {
          console.log(`  ${ICON.ok}  ${pc.bold(file.skillId.padEnd(24))} ${pc.dim(file.path)}`);
        }
      }
    });

  skills
    .command("discover-local")
    .description("Inventory local skill files and optionally fold selected ones into the OrgX base skill extension.")
    .option("--from <sources>", "local sources to scan: opencode, claude, codex, agents, workspace, or all", "all")
    .option("--context <text>", "workflow context used to rank and annotate discovered skills")
    .option("--limit <count>", "max candidates to show", "12")
    .option("--apply <selection>", "comma-separated candidate numbers or ids to opt into the OrgX base skill extension")
    .option("--scope <scope>", "extension scope: user, workspace, or project", "user")
    .option("--json", "emit a JSON summary")
    .action(async (options: {
      apply?: string;
      context?: string;
      from?: string;
      json?: boolean;
      limit?: string;
      scope?: "user" | "workspace" | "project";
    }) => {
      const sources = parseLocalSkillSources(options.from);
      const limit = parsePositiveInteger(options.limit, 12, "--limit");
      const candidates = discoverLocalSkills({
        ...(options.context?.trim() ? { context: options.context.trim() } : {}),
        limit,
        sources,
      });
      let extensionWrite: SkillExtensionWrite | null = null;
      if (options.apply?.trim()) {
        const selected = selectLocalSkillCandidates(candidates, options.apply);
        extensionWrite = writeLocalSkillSelection({
          candidates: selected,
          ...(options.context?.trim() ? { context: options.context.trim() } : {}),
          scope: options.scope ?? "user",
        });
      }

      if (options.json) {
        console.log(JSON.stringify({
          applied: extensionWrite
            ? {
                changed: extensionWrite.changed,
                created: extensionWrite.created,
                path: extensionWrite.path,
              }
            : null,
          candidates,
        }, null, 2));
        return;
      }

      printLocalSkillCandidates(candidates);
      if (extensionWrite) {
        console.log("");
        printSkillExtensionWrite(extensionWrite);
        console.log(
          `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply selected local preferences to configured tools.`)}`,
        );
      } else if (candidates.length > 0) {
        console.log("");
        console.log(
          `  ${ICON.skip}  ${pc.dim(`Opt in with ${getCmd()} skills discover-local --apply 1,2, then run ${getCmd()} skills sync.`)}`,
        );
      }
    });

  const skillExtensions = skills
    .command("extensions")
    .description("Create, edit, and sync user extensions appended after OrgX core skills.");

  skillExtensions
    .command("list")
    .description("List local OrgX skill extensions.")
    .action(() => {
      printSkillExtensions(listSkillExtensions());
    });

  skillExtensions
    .command("add")
    .description("Create a local extension file for an OrgX skill.")
    .argument("<skill>", "Skill id, for example orgx, cursor-rules, or morning-briefing")
    .option("--scope <scope>", "Extension scope: user, workspace, or project.", "user")
    .option("--title <title>", "Extension title.")
    .option("--content <content>", "Initial extension body content.")
    .option("--overwrite", "Overwrite an existing extension file.")
    .action((skill: string, options: {
      content?: string;
      overwrite?: boolean;
      scope?: "user" | "workspace" | "project";
      title?: string;
    }) => {
      const result = addSkillExtension({
        overwrite: options.overwrite === true,
        skillId: skill,
        ...(options.content !== undefined ? { content: options.content } : {}),
        ...(options.scope !== undefined ? { scope: options.scope } : {}),
        ...(options.title !== undefined ? { title: options.title } : {}),
      });
      printSkillExtensionWrite(result);
      console.log(
        `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply it to configured tools.`)}`,
      );
    });

  skillExtensions
    .command("edit")
    .description("Create if needed, then open a local OrgX skill extension in $EDITOR.")
    .argument("<skill>", "Skill id, for example orgx, cursor-rules, or morning-briefing")
    .option("--scope <scope>", "Extension scope: user, workspace, or project.", "user")
    .action((skill: string, options: { scope?: "user" | "workspace" | "project" }) => {
      const result = addSkillExtension({
        skillId: skill,
        ...(options.scope !== undefined ? { scope: options.scope } : {}),
      });
      printSkillExtensionWrite(result);
      if (!openPathInEditor(result.path)) {
        console.log(
          `  ${ICON.warn}  ${pc.yellow("editor not opened")} ${pc.dim(`Set EDITOR or edit ${result.path}`)}`,
        );
      }
      console.log(
        `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply it to configured tools.`)}`,
      );
    });

  skillExtensions
    .command("enable")
    .description("Enable a local OrgX skill extension.")
    .argument("<skill>", "Skill id, for example orgx, cursor-rules, or morning-briefing")
    .option("--scope <scope>", "Extension scope: user, workspace, or project.", "user")
    .action((skill: string, options: { scope?: "user" | "workspace" | "project" }) => {
      const result = setSkillExtensionEnabled({
        enabled: true,
        skillId: skill,
        ...(options.scope !== undefined ? { scope: options.scope } : {}),
      });
      printSkillExtensionWrite(result);
      console.log(
        `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply it to configured tools.`)}`,
      );
    });

  skillExtensions
    .command("disable")
    .description("Disable a local OrgX skill extension without deleting it.")
    .argument("<skill>", "Skill id, for example orgx, cursor-rules, or morning-briefing")
    .option("--scope <scope>", "Extension scope: user, workspace, or project.", "user")
    .action((skill: string, options: { scope?: "user" | "workspace" | "project" }) => {
      const result = setSkillExtensionEnabled({
        enabled: false,
        skillId: skill,
        ...(options.scope !== undefined ? { scope: options.scope } : {}),
      });
      printSkillExtensionWrite(result);
      console.log(
        `  ${ICON.skip}  ${pc.dim(`Run ${getCmd()} skills sync to apply it to configured tools.`)}`,
      );
    });

  await program.parseAsync(process.argv);
}

main().catch(async (error) => {
  await captureWizardException(error);
  console.error(pc.red(error instanceof Error ? error.message : String(error)));
  process.exitCode = 1;
});
